import {
  Component,
  Inject,
  Optional,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  OnInit,
  OnDestroy
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { SharingPatientId } from '../sharing-setup/sharing-setup.component';
import { PatientOverviewFacade } from '../patient-overview/patient-overview.facade';
import { PatientOverviewData } from '../patient-overview/patient-overview.models';
import { MailerService } from 'src/app/services/mailer.service';
import { CreateFacilityUserComponent } from '../create-facility-user/create-facility-user.component';
import { CreatepatientComponent } from '../createpatient/createpatient.component';
import { EditClientFormComponent } from '../edit-client-form/edit-client-form.component';
import { FacilitatorInfo, FacilityInfo } from '../transfer-visualization/transfer-visualization.component';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { FirebaseUtilitiesService, Facility } from 'src/app/services/firebase-utilities.service';
import { UIMessagingService } from 'src/app/services/uimessaging.service';
import { environment } from 'src/environments/environment';
import { SharingSetupComponent } from '../sharing-setup/sharing-setup.component';
import { AuthService } from 'src/app/services/auth.service';
import { OptionChooserComponent } from '../ui/optionChooser/optionChooser.component';
import { ChangeTargetDialogComponent, ChangeTargetDialogData } from '../change-target-dialog/change-target-dialog.component';
import { UserPlans } from 'src/app/dictionaries/UserPlans';
import { StudyShareService, StudyShare } from 'src/app/services/study-share.service';
import { StudySharePatientHistoryModalComponent, PatientShareHistoryModalData } from 'src/app/studies/components/study-share-patient-history-modal/study-share-patient-history-modal.component';

@Component({
    selector: 'app-patient-overview-v2',
    templateUrl: './patient-overview-v2.component.html',
    styleUrls: ['./patient-overview-v2.component.scss'],
    providers: [PatientOverviewFacade],
    standalone: false
})
export class PatientOverviewV2Component implements OnInit, OnDestroy {
  @ViewChild('imageShareRight', { static: false }) imageShareRight!: ElementRef;
  @ViewChild('orthancFrame', { static: false }) orthancFrame?: ElementRef<HTMLIFrameElement>;
  @ViewChild(SharingSetupComponent, { static: false }) sharingSetupComponent?: SharingSetupComponent;
  isLoading = false;
  hasError = false;
  errorMessage = '';
  isRouteMode = false; // Track if component is used as a route (not a dialog)
  private destroy$ = new Subject<void>(); // For cleanup
  private isNavigatingInternally = false; // Flag to prevent re-initialization during internal navigation
  private isInitializingFromUrl = false; // Flag to prevent duplicate calls to initializeFromUrl()
  private isPageInitialized = false; // Flag to track if page has been initialized correctly

  // Local storage for selected target facility (since new facade doesn't have this property)
  private _selectedTargetFacility: SharingPatientId | null = null;
  /** True when the studies page has loaded results spanning more than one unique patient. */
  studiesHaveMultiplePatients = false;
  /** Patient derived from the studies results when all results are for the same patient. */
  studiesFoundPatient: PatientOverviewData['patient'] | null = null;
  /** null = checking, true = exists in Firestore, false = not found → show Create Patient */
  studiesFoundPatientExists: boolean | null = null;

  // Flag to track if we're showing studies page from Back to Patient Overview event
  private _forceStudiesTab: boolean = false;
    /** When true, the user has explicitly switched to Documents Exchange via the breadcrumb.
   *  All URL-based "force to image mode" code paths must check this flag and bail out. */
  private _documentExchangeActive = false;
  /** When true, shows the inline facility picker in the sidebar instead of the SOURCE→TARGET animation. */
  showChangeTargetPicker = false;
  // Track which document component to show in right zone
  activeDocumentComponent: 'share' | 'retrieve' | null = null;

  private backToPatientOverviewHandler?: (event: Event) => void;
  private externalCancelHandler?: (event: Event) => void;
  private lastBtpoEventTimestamp: number = 0;
  private readonly BTPO_DEBOUNCE_MS = 2000; // Prevent multiple calls within 2 seconds
  private isProcessingBtpo: boolean = false; // Flag to prevent concurrent processing
  private btpoDeferredTimer: any = null;

  /** Latest share per target facility for the current patient. Populated for Hub users only. */
  patientSharesByFacility: Map<string, StudyShare> = new Map();
  patientSharesLoaded = false;

  constructor(
    @Optional() public dialogRef: MatDialogRef<PatientOverviewV2Component>,
    private cdr: ChangeDetectorRef,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: PatientOverviewData | null,
    @Inject(PatientOverviewFacade) private facade: PatientOverviewFacade,
    public route: ActivatedRoute,
    public router: Router,
    private location: Location,
    private firebaseUtilities_$: FirebaseUtilitiesService,
    private dialog: MatDialog,
    private mailer_$: MailerService,
    private authService:AuthService,
    private localStorageService: LocalStorageService,
    private uiMessaging_$: UIMessagingService,
    private studyShareService: StudyShareService
  ) {
    // Check if we have a caseName in route params (indicates route usage)
    const caseName = this.route.snapshot.paramMap.get('caseName') || this.route.snapshot.params['caseName'];

    // Determine if we're being used as a route vs dialog
    const isRouteUsage =
      !!caseName || !this.dialogRef || (this.dialogRef && typeof this.dialogRef.beforeClosed !== 'function');
    this.isRouteMode = isRouteUsage; // Store for use in template

    // If used as a route (no real dialogRef), initialize loading state immediately
    if (isRouteUsage && !this.data) {
      this.isLoading = true;
    }

    // Set up extension event listener for route mode
    if (this.isRouteMode) {
      this.setupExtensionEventListener();
      // Also listen for window focus/visibility changes to check for pending BtPO flag
      this.setupVisibilityChangeListener();
    }

    // CRITICAL: If we're on /exchange/:facilityID route, set page to 2 immediately in constructor
    // This ensures the page is set before any template rendering happens
    if (isRouteUsage) {
      const currentUrl = this.router.url;
      if (currentUrl.includes('/exchange/') && !currentUrl.includes('/type') && !currentUrl.includes('/images')) {
        // Check if we have a facilityID in the route
        const facilityID = this.route.snapshot.paramMap.get('facilityID') || this.route.snapshot.params['facilityID'];
        if (facilityID) {
          // Set page to 2 immediately - this happens before ngOnInit and template rendering
          this.facade.currentPage = 2;
          (this.vm as any).currentPage = 2;
          this.isPageInitialized = true;
        }
      }
    }

    // Set up navigation monitoring IMMEDIATELY in constructor for route mode
    // This must happen before any async operations to catch early redirects
    if (this.isRouteMode) {
      // Monitor Angular router events
      this.router.events
        .pipe(
          filter(event => event instanceof NavigationEnd),
          takeUntil(this.destroy$)
        )
        .subscribe((event: NavigationEnd) => {
          const isNavigatingAway = !event.url.startsWith('/patient-overview');

          if (isNavigatingAway) {
            console.error('PatientOverviewV2Component: REDIRECT DETECTED via Router!', {
              from: this.router.url,
              to: event.url,
              urlAfterRedirects: event.urlAfterRedirects,
              isRouteMode: this.isRouteMode,
              timestamp: new Date().toISOString()
            });
          } else {
            // If navigating internally to /images, ensure page is set to 4 and studies page is shown
            if (this.isNavigatingInternally && event.url.includes('/images')) {
              if ((this.vm as any).currentPage !== undefined) {
                (this.vm as any).currentPage = 4;
              }
              this.facade.currentPage = 4;

              // If URL includes /send or /retreive, ensure studies page is shown
              if (event.url.includes('/images/send') || event.url.includes('/images/retreive')) {
                this.ensureStudiesPageIsShown();
              }

              this.cdr.detectChanges();
            } else if (event.url.includes('/images/send') || event.url.includes('/images/retreive')) {
              // Even if not navigating internally, if URL includes /images/send or /retreive, show studies page
              this.ensureStudiesPageIsShown();
            }
          }
        });

      // Monitor window location changes (catches location.reload(), location.replace(), etc.)
      const initialUrl = window.location.href;
      const checkInterval = setInterval(() => {
        if (window.location.href !== initialUrl && !window.location.href.includes('patient-overview')) {
          console.error('PatientOverviewV2Component: REDIRECT DETECTED via window.location!', {
            initialUrl,
            currentUrl: window.location.href,
            pathname: window.location.pathname,
            isRouteMode: this.isRouteMode,
            timestamp: new Date().toISOString()
          });
          clearInterval(checkInterval);
        }
      }, 100);

      // Clean up interval on destroy
      this.destroy$.subscribe(() => clearInterval(checkInterval));
    }
  }

  onBackToPatientOverview() {
    this.router.navigate(['/patient-overview', this.vm.patient?.caseName]);
  }

  onStudiesChanged(studies: any[]): void {
    if (!studies || studies.length === 0) {
      this.studiesHaveMultiplePatients = false;
      this.studiesFoundPatient = null;
      this.cdr.detectChanges();
      return;
    }
    const firstId = (studies[0].patientId || '').toLowerCase();
    this.studiesHaveMultiplePatients = !studies.every((s: any) => (s.patientId || '').toLowerCase() === firstId);

    if (!this.studiesHaveMultiplePatients) {
      const s = studies[0];
      const rawName = s.patientName || '';
      let lastname = '';
      let firstname = '';
      if (rawName.includes('^')) {
        // Standard DICOM format: LAST^FIRST^MIDDLE^...
        const nameParts = rawName.split('^');
        lastname = nameParts[0] || '';
        firstname = nameParts[1] || '';
      } else if (rawName.includes(',')) {
        // Alternate format: LAST, FIRST
        const commaIdx = rawName.indexOf(',');
        lastname = rawName.substring(0, commaIdx).trim();
        firstname = rawName.substring(commaIdx + 1).trim();
      } else {
        lastname = rawName;
      }
      let dob = s.patientBirthDate || '';
      if (dob.length === 8 && !dob.includes('-')) {
        dob = `${dob.substring(0, 4)}-${dob.substring(4, 6)}-${dob.substring(6, 8)}`;
      }
      this.studiesFoundPatient = {
        clientname: '',
        firstname,
        lastname,
        dob: dob || this.vm.patient?.dob || '',
        gender: s.patientSex || this.vm.patient?.gender || '',
        patientId: s.patientId || '',
        caseName: s.patientId || '',
        ownerID: s.patientId || '',
        type: 'medical'
      };
      this.studiesFoundPatientExists = null; // checking
      this.cdr.detectChanges();
      this.checkFoundPatientExists(s.patientId || '');
    } else {
      this.studiesFoundPatient = null;
      this.studiesFoundPatientExists = null;
    }
    this.cdr.detectChanges();
  }

  private async checkFoundPatientExists(patientId: string): Promise<void> {
    const facilityId = this.vm.currentFacility;
    // facade.initialize() is async (awaits modalities + facilities). Studies can load before
    // it completes, leaving vm.patient null. Fall back to currentUid so we still query the
    // logged-in user's own patients rather than defaulting to "not found".
    const currentUid = (this.authService.userData.value as any)?.uid;
    const ownerID = this.vm.patient?.ownerID || currentUid;
    if (!patientId || !facilityId || !ownerID) {
      this.studiesFoundPatientExists = false;
      this.cdr.detectChanges();
      return;
    }
    // Check vm.patient in-memory first (already loaded, no extra Firestore query).
    // Covers: Hub owner viewing their own patient, Spoke viewing a Hub-shared patient whose
    // Spoke MRN is stored in metadata.medical_record_numbers, and the race-condition window
    // where vm.patient.caseName / patientID directly matches the DICOM patientId.
    if (this.vm.patient) {
      const normalisedId = patientId.trim().toLowerCase();
      const toCheck = [
        this.vm.patient.caseName,
        this.vm.patient.patientId,
        this.vm.patient['patientID'],   // capital-D variant stored by createMedicalPatient
      ];
      if (toCheck.some(v => (v || '').trim().toLowerCase() === normalisedId)) {
        this.studiesFoundPatientExists = true;
        this.cdr.detectChanges();
        return;
      }
      const mrns: any[] = this.vm.patient.metadata?.medical_record_numbers || this.vm.patient.patientIDs || [];
      if (mrns.some((m: any) => (m.value || '').trim().toLowerCase() === normalisedId)) {
        this.studiesFoundPatientExists = true;
        this.cdr.detectChanges();
        return;
      }
    }

    const isSpokeContext = !!(currentUid && currentUid !== this.vm.patient?.ownerID);
    // Spoke users query their own patient list (currentUid) so they can determine whether to
    // show "Create Patient" for a DICOM patient that doesn't yet exist in their Firestore.
    // Hub owners (and the fallback when vm.patient is null) query by ownerID.
    const ownerToQuery = isSpokeContext ? currentUid : ownerID;
    try {
      this.studiesFoundPatientExists = await this.firebaseUtilities_$.patientExistsByMrn(patientId, facilityId, ownerToQuery);
    } catch (e) {
      console.error('[patient-overview-v2] patientExistsByMrn failed:', e);
      this.studiesFoundPatientExists = false;
    }
    this.cdr.detectChanges();
  }

  async openCreatePatientFromDicom(): Promise<void> {
    if (!this.studiesFoundPatient) return;
    let facilitiesData: any[] = [];
    try {
      const facilities = await this.firebaseUtilities_$.getFacilities();
      facilitiesData = facilities.map((f: any) => ({ id: f.id, name: f.name }));
    } catch (e) {
      console.error('[patient-overview-v2] Failed to fetch facilities for create patient dialog', e);
    }
    this.dialog.open(CreatepatientComponent, {
      width: '550px',
      data: {
        facilities: facilitiesData,
        prefill: {
          firstname: this.studiesFoundPatient.firstname,
          lastname: this.studiesFoundPatient.lastname,
          dob: this.studiesFoundPatient.dob,
          gender: this.studiesFoundPatient.gender,
          patientId: this.studiesFoundPatient.patientId
        }
      }
    });
  }

  async ngOnInit(): Promise<void> {
    // Check if we have a caseName in route params (indicates route usage)
    const caseName = this.route.snapshot.paramMap.get('caseName') || this.route.snapshot.params['caseName'];

    // Determine if we're being used as a route vs dialog
    // Route usage: has caseName param OR (no dialogRef or dialogRef doesn't have beforeClosed method)
    const isRouteUsage = !!caseName || !this.dialogRef || typeof this.dialogRef.beforeClosed !== 'function';

    // Check if data has valid patient data (has caseName property matching route param)
    const hasValidPatientData =
      this.data &&
      this.data.patient &&
      this.data.patient.caseName &&
      (!caseName || this.data.patient.caseName === caseName);

    // CRITICAL: Restore facility from URL IMMEDIATELY if present (before template renders)
    // This ensures the Continue button appears when the component first renders
    if (isRouteUsage && caseName) {
      const currentUrl = this.router.url;
      if (currentUrl.includes('/exchange/')) {
        const facilityID = this.route.snapshot.paramMap.get('facilityID') ||
          this.route.snapshot.params['facilityID'] ||
          (() => {
            const facilityMatch = currentUrl.match(/\/exchange\/([^\/]+)/);
            return facilityMatch && facilityMatch[1] ? facilityMatch[1] : null;
          })();

        if (facilityID) {
          // Try to restore from localStorage immediately
          const savedState = this.localStorageService.getPatientOverviewState(caseName);
          if (savedState?.selectedTargetFacility) {
            const normalizedFacilityId = facilityID.toLowerCase().trim();
            const savedFacilityId = savedState.selectedTargetFacility.facility?.toLowerCase().trim();
            if (savedFacilityId === normalizedFacilityId ||
              savedFacilityId?.replace(/-/g, '') === normalizedFacilityId.replace(/-/g, '')) {
              // Set immediately so template can see it on first render
              this._selectedTargetFacility = savedState.selectedTargetFacility;

              // CRITICAL: If we're on /exchange/:facilityID and have a facility, set page to 2 immediately
              // This prevents any flash of page 1 before facade.initialize() runs
              const currentUrl = this.router.url;
              if (currentUrl.includes('/exchange/') && !currentUrl.includes('/type') && !currentUrl.includes('/images')) {
                // Set page to 2 before facade.initialize() to prevent flash of page 1
                // Always set it, even if undefined (it will be defined after facade.initialize())
                // CRITICAL: Set facade.currentPage first, then vm (since vm is a getter)
                this.facade.currentPage = 2;
                (this.vm as any).currentPage = 2;
                this.isPageInitialized = true;
              }

              // Mark for check to ensure Angular detects the change
              this.cdr.markForCheck();
            }
          }
        }
      }
    }

    // PRIORITY 1: If used as a route with caseName, ALWAYS load from route parameter
    // This ensures we always have fresh data from the database
    if (caseName && isRouteUsage) {
      this.isLoading = true;
      this.cdr.detectChanges();

      try {
        // Try original case first, then lowercase as fallback
        let patientData = await this.authService.getPatientOverviewData(caseName);
        if (!patientData && caseName !== caseName.toLowerCase()) {
          patientData = await this.authService.getPatientOverviewData(caseName.toLowerCase());
        }

        if (patientData) {
          this.data = { patient: patientData };
          try {
            // CRITICAL: Check URL BEFORE initializing facade to determine if we should preserve page 2 or 3
            const currentUrl = this.router.url;
            const shouldPreservePage3 = currentUrl.includes('/type');
            const shouldPreservePage2 = currentUrl.includes('/exchange/') && !currentUrl.includes('/type') && !currentUrl.includes('/images');
            let savedStateForPage3: any = null;
            let savedStateForPage2: any = null;

            if (shouldPreservePage3) {
              // Get saved state BEFORE facade initialization (which calls resetState())
              const patientID = this.route.snapshot.paramMap.get('caseName');
              if (patientID) {
                savedStateForPage3 = this.localStorageService.getPatientOverviewState(patientID);
              }
            }

            if (shouldPreservePage2) {
              // Get saved state BEFORE facade initialization (which calls resetState())
              const patientID = this.route.snapshot.paramMap.get('caseName');
              if (patientID) {
                savedStateForPage2 = this.localStorageService.getPatientOverviewState(patientID);
              }
            }

            this.facade.initialize(null, this.data).then(() => {
              // CRITICAL: resetState() inside initialize() runs after getFacilities() resolves
              // (async). If it runs after the route-params subscription / 300ms timeout have
              // already set isImageShareMode=true, it resets it back to false. Re-apply the
              // studies-page state here so it is always set AFTER resetState() completes.
              const urlAfterInit = this.router.url;
              if (urlAfterInit.includes('/images/send') || urlAfterInit.includes('/images/retreive')) {
                this.ensureStudiesPageIsShown();
                // vm.facilities is now populated — resolve the Hub facility from the URL
                // segment so targetFacilityName renders correctly in the studies-page title.
                this.restoreFacilityFromUrl(urlAfterInit);
                this.cdr.detectChanges();
              }
              this.loadPatientShares();
            });
            this.isLoading = false;

            // CRITICAL: If URL includes /exchange/:facilityID (but not /type or /images), set page to 2 IMMEDIATELY
            // This provides seamless auto-advance when a facility is selected
            if (shouldPreservePage2 && savedStateForPage2) {
              const hasFacility = savedStateForPage2.selectedTargetFacility;
              if (hasFacility) {
                // Set on both vm and facade synchronously
                // CRITICAL: vm is a getter that returns facade, so setting facade.currentPage is enough
                // But we also set (this.vm as any).currentPage to ensure the template binding works
                this.facade.currentPage = 2;
                (this.vm as any).currentPage = 2;
                this.isPageInitialized = true;

                // Force change detection immediately - this must happen before template renders
                this.cdr.markForCheck();
                this.cdr.detectChanges();
              }
            }

            // CRITICAL: If URL includes /type, set page to 2 IMMEDIATELY after facade initialization
            // This must happen synchronously, before any template rendering or async operations
            // Page 2 is now exchange type selection (moved from page 3)
            if (shouldPreservePage3 && savedStateForPage3) {
              // Page 2 (exchange type) doesn't require users, so we can set it if facility is selected
              const hasFacility = savedStateForPage3.selectedTargetFacility;
              if (hasFacility) {
                // Set on both vm and facade synchronously
                (this.vm as any).currentPage = 2;
                this.facade.currentPage = 2;

                // Force change detection immediately - this must happen before template renders
                this.cdr.markForCheck();
                this.cdr.detectChanges();
              }
            }

            // Initialize decision screen state - show decision screen first if not in exchange flow
            if (!currentUrl.includes('/exchange')) {
              if ((this.vm as any).showDecisionScreen !== undefined) {
                (this.vm as any).showDecisionScreen = true;
              }
              if ((this.vm as any).showSharingSetup !== undefined) {
                (this.vm as any).showSharingSetup = false;
              }
            }

            // Initialize from URL after facade is initialized and patient data is loaded
            // Use setTimeout to ensure facade and vm are fully initialized
            setTimeout(() => {
              this.initializeFromUrl();

              // CRITICAL: After initializeFromUrl(), if we're on /type URL, ensure page stays at 2
              // The facade's resetState() might have reset it to 1 during initialization
              // Page 2 is now exchange type selection (moved from page 3)
              const currentUrl = this.router.url;
              if (currentUrl.includes('/type')) {
                // Check if facility is selected (required for page 2)
                const hasFacility = !!this._selectedTargetFacility;

                if (hasFacility) {
                  const currentPage = (this.vm as any).currentPage || this.facade.currentPage;
                  if (currentPage !== 2) {
                    (this.vm as any).currentPage = 2;
                    this.facade.currentPage = 2;
                  }

                  // Also set it again after a short delay to catch any late resets
                  setTimeout(() => {
                    const pageAfterDelay = (this.vm as any).currentPage || this.facade.currentPage;
                    if (pageAfterDelay !== 2) {
                      (this.vm as any).currentPage = 2;
                      this.facade.currentPage = 2;
                      this.cdr.detectChanges();
                    }
                  }, 50);
                } else {
                  console.warn('PatientOverviewV2Component: Cannot set page to 2 - no facility selected', {
                    _selectedTargetFacility: this._selectedTargetFacility
                  });
                }
              }

              this.cdr.detectChanges();
            }, 0);

            // Subscribe to route parameter changes for nested routes
            // Only re-initialize if we have patient data loaded to prevent unnecessary reloads
            this.route.params.pipe(takeUntil(this.destroy$)).subscribe(() => {
              // Skip if we're navigating internally (to prevent re-initialization)
              if (this.isNavigatingInternally) {
                // Don't reset the flag here, let the navigation promise handle it
                return;
              }
              // Check both vm.patient and data.patient since vm might not be updated immediately
              const hasPatientData = (this.vm.patient && this.vm.patient.caseName) || (this.data?.patient && this.data.patient.caseName);
              if (hasPatientData) {
                const currentUrl = this.router.url;
                const currentPage = (this.vm as any).currentPage || this.facade.currentPage;

                // CRITICAL: If URL includes /images, don't re-run initializeFromUrl()
                // (it can fight with the user's current state / cause resets). Instead,
                // keep page=4 and, for directional routes, force fullscreen + studies tab.
                if (currentUrl.includes('/images') && !this._documentExchangeActive) {
                  // Ensure page is still 4 (in case something reset it)
                  if (currentPage !== 4) {
                    if ((this.vm as any).currentPage !== undefined) {
                      (this.vm as any).currentPage = 4;
                    }
                    this.facade.currentPage = 4;
                  }

                  // If we're on a directional images route, we must be fullscreen and show studies-page.
                  // This is required for the "Back to Patient Overview" flow coming from the external app.
                  const isDirectionalImagesRoute =
                    currentUrl.includes('/images/send') || currentUrl.includes('/images/retreive');
                  if (isDirectionalImagesRoute) {
                    // CRITICAL: ALWAYS set these values - this is non-negotiable for /images routes
                    this.vm.isImageShareMode = true;
                    this.vm.isIframeLoading = false; // studies-page is a component

                    if ((this.vm as any).iframeError !== undefined) {
                      (this.vm as any).iframeError = null;
                    }

                    // Ensure shareTypeSelected is 'image' for fullscreen mode
                    this.vm.shareTypeSelected = 'image';
                    this.facade.shareTypeSelected = 'image';

                    // CRITICAL: Force studies tab - this ensures the getter returns 'studies'
                    this._forceStudiesTab = true;
                    (this.vm as any).activeImageShareTab = 'studies';
                    if ((this.facade as any).activeImageShareTab !== undefined) {
                      (this.facade as any).activeImageShareTab = 'studies';
                    }

                    if ((this.vm as any).shareDirection !== undefined) {
                      (this.vm as any).shareDirection = currentUrl.includes('/images/retreive') ? 'receive' : 'send';
                    }

                    // CRITICAL: Force page to 4 for /images routes to bypass facility selection
                    // This ensures the studies page is shown directly
                    if ((this.vm as any).currentPage !== undefined) {
                      (this.vm as any).currentPage = 4;
                    }
                    this.facade.currentPage = 4;

                    // Force change detection to ensure template updates
                    this.cdr.markForCheck();
                    this.cdr.detectChanges();
                  }

                  this.cdr.detectChanges();
                  return;
                }

                // CRITICAL: If URL includes /type and we're already on page 3, don't re-initialize
                // This prevents resetting the page when route params subscription fires
                if (currentUrl.includes('/type') && currentPage === 2) {
                  // Just ensure page is still 2 (defensive check)
                  // Page 2 is now exchange type selection (moved from page 3)
                  if ((this.vm as any).currentPage !== 2) {
                    (this.vm as any).currentPage = 2;
                  }
                  if (this.facade.currentPage !== 2) {
                    this.facade.currentPage = 2;
                  }
                  this.cdr.detectChanges();
                  return;
                }

                // Check if we're on page 2 or higher with a facility selected - don't reset to page 1
                // BUT: Allow initializeFromUrl() if URL includes /type (to set page to 3) or /images (to set page to 4)
                if (currentPage >= 2 && currentUrl.includes('/exchange/') && this._selectedTargetFacility &&
                  !currentUrl.includes('/type') && !currentUrl.includes('/images')) {
                  return;
                }

                this.initializeFromUrl();

                // CRITICAL: After initializeFromUrl(), if we're on /type URL, ensure page stays at 2
                // This handles the case where initializeFromUrl() might have been called by route params subscription
                if (currentUrl.includes('/type')) {
                  const hasFacility = !!this._selectedTargetFacility;
                  if (hasFacility) {
                    const pageAfterInit = (this.vm as any).currentPage || this.facade.currentPage;
                    if (pageAfterInit !== 2) {
                      (this.vm as any).currentPage = 2;
                      this.facade.currentPage = 2;
                    }
                  }
                }

                this.cdr.detectChanges();
              }
            });
            // CRITICAL: After component initialization, if URL includes /images, ensure studies page is shown
            // Use this.router.url directly to avoid redeclaration of currentUrl
            const imagesUrl = this.router.url;
            if (imagesUrl.includes('/images/send') || imagesUrl.includes('/images/retreive')) {
              setTimeout(() => {
                // Force state to ensure studies page is shown
                this.vm.isImageShareMode = true;
                this._forceStudiesTab = true;
                (this.vm as any).activeImageShareTab = 'studies';
                if ((this.facade as any).activeImageShareTab !== undefined) {
                  (this.facade as any).activeImageShareTab = 'studies';
                }
                this.vm.shareTypeSelected = 'image';
                this.facade.shareTypeSelected = 'image';

                // Force change detection
                this.cdr.markForCheck();
                this.cdr.detectChanges();

              }, 300);
            }
            // After initialization, if URL includes /send-documents or /retrieve-documents, restore document state
            if (imagesUrl.includes('/send-documents') || imagesUrl.includes('/retrieve-documents')) {
              setTimeout(() => {
                const docDir: 'send' | 'receive' = imagesUrl.includes('/send-documents') ? 'send' : 'receive';
                this._documentExchangeActive = true;
                this.vm.shareTypeSelected = 'document';
                this.facade.shareTypeSelected = 'document';
                if ((this.vm as any).shareDirection !== undefined) {
                  (this.vm as any).shareDirection = docDir;
                }
                this.vm.isImageShareMode = true;
                this.cdr.markForCheck();
                this.cdr.detectChanges();
              }, 300);
            }
          } catch (initError) {
            console.error('PatientOverviewV2Component: Error initializing facade:', initError);
            this.hasError = true;
            this.errorMessage = `Error initializing component: ${initError.message || 'Unknown error'}`;
            this.isLoading = false;
            this.cdr.detectChanges();
          }
        } else {
          console.warn('PatientOverviewV2Component: Patient data is null or undefined');
          this.hasError = true;
          this.errorMessage = `Patient data not found for case name: ${caseName}. Please verify the case name is correct.`;
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      } catch (error) {
        console.error('PatientOverviewV2Component: Error loading patient data:', error);
        this.hasError = true;
        this.errorMessage = `Error loading patient data: ${error.message || 'Unknown error'}. Please verify the case name "${caseName}" is correct.`;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    } else if (this.dialogRef && typeof this.dialogRef.beforeClosed === 'function' && this.data) {
      // If used as a dialog (has real MatDialogRef with beforeClosed method), use the existing initialization
      this.facade.initialize(this.dialogRef, this.data);

      // Initialize decision screen state - show decision screen first if not in exchange flow
      const currentUrl = this.router.url;
      if (!currentUrl.includes('/exchange')) {
        if ((this.vm as any).showDecisionScreen !== undefined) {
          (this.vm as any).showDecisionScreen = true;
        }
        if ((this.vm as any).showSharingSetup !== undefined) {
          (this.vm as any).showSharingSetup = false;
        }
      }

      this.initializeFromUrl();
      this.isLoading = false;

      // Subscribe to route parameter changes for nested routes
      // Only re-initialize if we have patient data loaded to prevent unnecessary reloads
      this.route.params.pipe(takeUntil(this.destroy$)).subscribe(() => {
        // Skip if we're navigating internally (to prevent re-initialization)
        if (this.isNavigatingInternally) {
          // Don't reset the flag here, let the navigation promise handle it
          return;
        }
        // Check both vm.patient and data.patient since vm might not be updated immediately
        const hasPatientData = (this.vm.patient && this.vm.patient.caseName) || (this.data?.patient && this.data.patient.caseName);
        if (hasPatientData) {
          // Check if URL includes /images and we're already on page 4 - don't re-initialize
          const currentUrl = this.router.url;
          if (currentUrl.includes('/images') && ((this.vm as any).currentPage === 4 || this.facade.currentPage === 4)) {
            return;
          }
          // Check if we're on page 2 or higher with a facility selected - don't reset to page 1
          // BUT: Allow initializeFromUrl() if URL includes /type (to set page to 3) or /images (to set page to 4)
          const currentPage = (this.vm as any).currentPage || this.facade.currentPage;
          if (currentPage >= 2 && currentUrl.includes('/exchange/') && this._selectedTargetFacility &&
            !currentUrl.includes('/type') && !currentUrl.includes('/images')) {
            return;
          }
          this.initializeFromUrl();
          this.cdr.detectChanges();
        }
      });
    } else if (isRouteUsage && hasValidPatientData && !caseName) {
      // If we have valid patient data but no caseName in route, use pre-loaded data
      // (This case should be rare - usually we'd have a caseName in the route)
      this.facade.initialize(null, this.data);

      // Initialize decision screen state - show decision screen first if not in exchange flow
      const currentUrl = this.router.url;
      if (!currentUrl.includes('/exchange')) {
        if ((this.vm as any).showDecisionScreen !== undefined) {
          (this.vm as any).showDecisionScreen = true;
        }
        if ((this.vm as any).showSharingSetup !== undefined) {
          (this.vm as any).showSharingSetup = false;
        }
      }

      this.initializeFromUrl();
      this.isLoading = false;
      this.cdr.detectChanges();
    } else if (isRouteUsage && !caseName) {
      // Route usage but no caseName - show error
      console.warn('PatientOverviewV2Component: Route usage but no caseName provided');
      this.hasError = true;
      this.errorMessage = 'Please provide a patient case name in the URL. Format: /patient-overview/CASE-NAME';
      this.isLoading = false;
      this.cdr.detectChanges();
    } else {
      // Unexpected state
      console.warn('PatientOverviewV2Component: Unexpected state', {
        hasDialogRef: !!this.dialogRef,
        hasData: !!this.data,
        caseName,
        isRouteUsage,
        hasValidPatientData
      });
      this.hasError = true;
      this.errorMessage = 'Unable to load patient overview. Please try again.';
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Cleanup event listeners when component is destroyed
    if (this.backToPatientOverviewHandler) {
      window.removeEventListener('nuagedx-back-to-patient-overview', this.backToPatientOverviewHandler);
    }
    if (this.externalCancelHandler) {
      window.removeEventListener('nuagedx-external-cancelled', this.externalCancelHandler);
    }
  }

  onSelectedPatientIdEvent(patientId: SharingPatientId): void {
    this.facade.onSelectedPatientIdEvent(patientId);
  }

  onSharingFacilitySelected(patientId: SharingPatientId): void {
    this.facade.onSharingFacilitySelected(patientId);
  }

  onShareWithFacility(): void {
    this.facade.onShareWithFacility();
  }

  onShareFromFacility(): void {
    this.facade.onShareFromFacility();
  }

  onSharingGoNext(): void {
    this.facade.onSharingGoNext();
    this.cdr.detectChanges();
  }

  onClose(): void {
    // Check if client profile is open before doing anything
    const clientProfileDialog = this.dialog.openDialogs.find(d => d.id === 'client-profile');

    // If client profile is open, don't close or navigate - let it handle its own lifecycle
    if (clientProfileDialog) {
      return;
    }

    // Always navigate to root when close button is clicked
    // Close dialog first if it exists and is a real dialog (not route mode)
    if (this.dialogRef && !this.isRouteMode && typeof this.dialogRef.close === 'function') {
      this.facade.onClose();
    }
    this.router
      .navigate(['/'])
      .then(() => {
      })
      .catch(err => {
        console.error('PatientOverviewV2Component: Navigation error', err);
      });
  }

  onBackToPatientsList(): void {
    // Close the dialog and navigate after it's fully closed
    this.router.navigateByUrl('/clientsmatters/external');
  }

  onBackToHome(): void {
    this.router.navigate(['/']).then(() => {
      this.authService.drawer?.open();
    });
  }

  onCreatePatient(): void {
    this.router.navigateByUrl('/cpatient');
  }

  onEditPatient(): void {
    if (!this.vm.patient) return;
    const userData = this.authService.userData.value as any;
    const config = new MatDialogConfig();
    config.autoFocus = true;
    config.data = {
      DateOfBirth: this.vm.patient.dob,
      FirstName: this.vm.patient.firstname,
      LastName: this.vm.patient.lastname,
      caseName: this.vm.patient.caseName,
      patientDocId: this.vm.patient.patientDocId || this.vm.patient.id || null,
      metadata: this.vm.patient.metadata,
      userFacility: userData?.facility || '',
      ownerID: userData?.ownerID || userData?.uid,
      patientPicture: this.vm.patient.patientPicture || this.vm.patient.metadata?.patientPicture || ''
    };
    const dialogRef = this.dialog.open(EditClientFormComponent, config);
    dialogRef.afterClosed().subscribe(result => {
      if (result === 1) {
        this.ngOnInit();
      }
    });
  }

  onChangePatient(): void {
    // Clear all state to restart from scratch
    // Clear local state
    this._selectedTargetFacility = null;
    this._forceStudiesTab = false;

    // Clear facade state
    if ((this.vm as any).showDecisionScreen !== undefined) {
      (this.vm as any).showDecisionScreen = true;
    }
    if ((this.vm as any).showSharingSetup !== undefined) {
      (this.vm as any).showSharingSetup = false;
    }
    if ((this.vm as any).shareTypeSelected !== undefined) {
      (this.vm as any).shareTypeSelected = null;
    }
    if ((this.vm as any).shareDirection !== undefined) {
      (this.vm as any).shareDirection = null;
    }
    this.facade.shareTypeSelected = null;
    this.facade.currentPage = 1;
    if ((this.vm as any).currentPage !== undefined) {
      (this.vm as any).currentPage = 1;
    }

    // Clear localStorage state for current patient
    const currentUrl = this.router.url;
    const urlMatch = currentUrl.match(/\/patient-overview\/([^\/]+)/);
    if (urlMatch && urlMatch[1]) {
      const patientID = urlMatch[1];
      this.localStorageService.clearPatientOverviewState(patientID);
    } else if (this.vm.patient?.caseName) {
      // Fallback: use caseName if URL parsing fails
      this.localStorageService.clearPatientOverviewState(this.vm.patient.caseName);
    }

    // Navigate to patients list to select a new patient
    this.router.navigateByUrl('/clientsmatters/external');
  }

  async onDocumentShare(): Promise<void> {
    return this.facade.onDocumentShare();
  }

  onImageShare(): void {
    this.facade.onImageShare();
    // Also call onShareTypeSelected to advance to page 4 (SEND/RETRIEVE selection)
    this.onShareTypeSelected('image');
  }

  onPageChange(page: number): void {
    // CRITICAL: If URL includes /images/send or /images/retreive, we're past the setup phase
    // Only force page 4 if we're in fullscreen mode or have already selected direction
    const currentUrl = this.getCurrentUrl();
    if (currentUrl.includes('/images/send') || currentUrl.includes('/images/retreive')) {
      // We're past the SEND/RECEIVE selection, force page 4
      if ((this.vm as any).currentPage !== undefined) {
        (this.vm as any).currentPage = 4;
      }
      this.facade.currentPage = 4;
      // Ensure shareTypeSelected is set
      if (this.facade.shareTypeSelected !== 'image') {
        this.facade.shareTypeSelected = 'image';
        if ((this.vm as any).shareTypeSelected !== undefined) {
          (this.vm as any).shareTypeSelected = 'image';
        }
      }
      this.cdr.detectChanges();
      return;
    }

    // If URL includes /images but not /send or /retreive, we're on page 3 (SEND/RECEIVE selection)
    // Allow navigation to page 3 in this case
    if (currentUrl.includes('/images') && page === 3) {
      // This is correct - we're navigating to page 3 for SEND/RECEIVE selection
      // Don't block this navigation
    } else if (currentUrl.includes('/images') && this.vm.isImageShareMode) {
      // If we're in fullscreen image share mode, don't allow navigation away
      return;
    }

    // Override the facade's onPageChange to handle correct flow based on our local state
    // The new facade's getMaxAllowedStep() doesn't check for selectedTargetFacility
    // so we need to handle page changes based on our local state

    // CRITICAL: If navigating to page 2 and URL already includes /type, allow it
    // This handles the case where the user clicks Continue from step 1 (facility selection)
    // Page 2 is now exchange type selection (moved from page 3)
    if (page === 2 && currentUrl.includes('/type')) {
      // Already on /type URL, just ensure page is 2
      const targetPage = 2;
      if ((this.vm as any).currentPage !== undefined) {
        (this.vm as any).currentPage = targetPage;
      }
      this.facade.currentPage = targetPage;
      this.cdr.detectChanges();
      return;
    }

    // Determine the max allowed page based on completed steps
    // New flow: 1=Facility, 2=Exchange Type, 3=SEND/RECEIVE, 4=Users (conditional)
    let maxAllowed = 1;

    // Step 1: Target facility selected
    if (this._selectedTargetFacility) {
      maxAllowed = Math.max(maxAllowed, 2);
    }

    // Step 2: Share type selected (moved from step 3)
    if ((this.vm as any).shareTypeSelected) {
      maxAllowed = Math.max(maxAllowed, 3);
    }

    // Step 3: Share direction selected (for images)
    // Page 4 (users) is only shown for image + send, so we need both conditions
    const isImageSend = (this.vm as any).shareTypeSelected === 'image' &&
      (this.vm as any).shareDirection === 'send';
    if (isImageSend) {
      maxAllowed = Math.max(maxAllowed, 4);
    }

    // Clamp the requested page to the max allowed
    const targetPage = page <= maxAllowed ? page : maxAllowed;

    // Keep URL in sync with step WITHOUT router navigation.
    // Router navigation would re-create the component (and re-fetch patient data) because routes differ.
    const urlMatch = currentUrl.match(/\/patient-overview\/([^\/]+)/);
    if (urlMatch && urlMatch[1] && this._selectedTargetFacility) {
      const patientID = urlMatch[1];
      const facilityID = this._selectedTargetFacility.facility;

      // Persist state so refresh/deep-link works.
      this.localStorageService.updatePatientOverviewState(patientID, {
        selectedTargetFacility: this._selectedTargetFacility,
        shareTypeSelected: (this.vm as any).shareTypeSelected || null,
        shareDirection: (this.vm as any).shareDirection || null
      });

      let targetUrl = '';
      if (targetPage === 1) {
        targetUrl = `/patient-overview/${patientID}/exchange/${facilityID}`;
      } else if (targetPage === 2) {
        // Page 2 is now exchange type, but URL structure might still use /type for page 3
        // We'll use /type for page 2 now, or keep /exchange for both 1 and 2
        targetUrl = `/patient-overview/${patientID}/exchange/${facilityID}/type`;
      } else if (targetPage === 3) {
        // Page 3 is SEND/RECEIVE, URL should include /images for image share
        if ((this.vm as any).shareTypeSelected === 'image') {
          targetUrl = `/patient-overview/${patientID}/exchange/${facilityID}/images`;
        } else {
          targetUrl = `/patient-overview/${patientID}/exchange/${facilityID}/type`;
        }
      } else if (targetPage === 4) {
        // Page 4 is users selection (only for image + send)
        targetUrl = `/patient-overview/${patientID}/exchange/${facilityID}/images/send`;
      }

      if (targetUrl && targetUrl !== currentUrl) {
        this.location.go(targetUrl);
      }
    }

    // Set the page directly to bypass facade's clamping logic
    this.facade.currentPage = targetPage;
    (this.vm as any).currentPage = targetPage;

    // Trigger change detection
    this.cdr.detectChanges();
  }

  onEditDestinationFacility(): void {
    // Clear the selected target facility
    this._selectedTargetFacility = null;

    // Clear shareTypeSelected and shareDirection since they depend on facility
    if ((this.vm as any).shareTypeSelected !== undefined) {
      (this.vm as any).shareTypeSelected = null;
    }
    this.facade.shareTypeSelected = null;

    if ((this.vm as any).shareDirection !== undefined) {
      (this.vm as any).shareDirection = null;
    }

    // Clear from localStorage
    const currentUrl = this.getCurrentUrl();
    const urlMatch = currentUrl.match(/\/patient-overview\/([^\/]+)/);
    const patientID = urlMatch?.[1];

    if (patientID) {
      this.localStorageService.updatePatientOverviewState(patientID, {
        selectedTargetFacility: null,
        shareTypeSelected: null,
        shareDirection: null
      });
    }

    // IMPORTANT: Directly set page to 1 and update URL without using onPageChange
    // because onPageChange has guards that prevent going back in certain scenarios
    this.facade.currentPage = 1;
    (this.vm as any).currentPage = 1;

    // Update URL to go back to /exchange (step 1 - facility selection)
    if (patientID) {
      const targetUrl = `/patient-overview/${patientID}/exchange`;
      if (targetUrl !== currentUrl) {
        this.location.go(targetUrl);
      }
    }

    // Trigger change detection
    this.cdr.detectChanges();
  }

  onClearTarget(): void {
    this._selectedTargetFacility = null;
    this.showChangeTargetPicker = false;

    const currentUrl = this.getCurrentUrl();
    const urlMatch = currentUrl.match(/\/patient-overview\/([^\/]+)/);
    if (urlMatch?.[1]) {
      this.localStorageService.updatePatientOverviewState(urlMatch[1], { selectedTargetFacility: null });
      const targetUrl = `/patient-overview/${urlMatch[1]}/exchange`;
      if (targetUrl !== currentUrl) {
        this.location.go(targetUrl);
      }
    }

    this.cdr.detectChanges();
  }

  onOpenChangeTargetPicker(): void {
    const dialogData: ChangeTargetDialogData = {
      facilities: this.vm.facilities,
      selectedFacility: this.selectedTargetFacility
    };
    const dialogRef = this.dialog.open(ChangeTargetDialogComponent, {
      data: dialogData,
      width: '480px',
      maxHeight: '80vh',
      panelClass: 'change-target-dialog-panel'
    });
    dialogRef.afterClosed().subscribe((facility: Facility | null) => {
      if (facility) {
        this.onChangeTargetFacilitySelected(facility);
      }
    });
  }

  onCancelChangeTargetPicker(): void {
    this.showChangeTargetPicker = false;
    this.cdr.detectChanges();
  }

  onChangeTargetFacilitySelected(facility: Facility): void {
    if (!facility?.id) {
      return;
    }
    const normalizedId = facility.id.toLowerCase().trim();
    const matchingPatientId =
      (this.vm.otherPatientIDs || []).find(pid => (pid.facility || '').toLowerCase().trim() === normalizedId) || null;

    const selected: SharingPatientId = matchingPatientId || { facility: facility.id, value: '', ownerId: '' };

    this.showChangeTargetPicker = false;
    this.onTargetFacilitySelected(selected);
  }

  onEditTargetFacilityFromSummary(): void {
    // Editing target facility should clear dependent steps (type/direction)
    // and take the user back to facility selection.
    this.onEditDestinationFacility();
  }

  onEditShareTypeFromSummary(): void {
    // Editing type of exchange should clear the selected type and jump back to step 2 (type selection).
    // Clear shareTypeSelected so no button appears selected when returning to step 2
    if ((this.vm as any).shareTypeSelected !== undefined) {
      (this.vm as any).shareTypeSelected = null;
    }
    this.facade.shareTypeSelected = null;

    // Also clear shareDirection since it depends on shareTypeSelected
    if ((this.vm as any).shareDirection !== undefined) {
      (this.vm as any).shareDirection = null;
    }

    // Clear from localStorage state
    const currentUrl = this.getCurrentUrl();
    const urlMatch = currentUrl.match(/\/patient-overview\/([^\/]+)/);
    if (urlMatch && urlMatch[1]) {
      const savedState = this.localStorageService.getPatientOverviewState(urlMatch[1]);
      if (savedState) {
        this.localStorageService.savePatientOverviewState(urlMatch[1], {
          ...savedState,
          shareTypeSelected: null,
          shareDirection: null
        });
      }
    }

    this.forceWizardStepForEdit(2);
  }

  onEditDirectionFromSummary(): void {
    // Editing direction should clear the selected direction and jump back to step 3 (SEND/RECEIVE selection).
    // Clear shareDirection so no button appears selected when returning to step 3
    if ((this.vm as any).shareDirection !== undefined) {
      (this.vm as any).shareDirection = null;
    }

    // Clear from localStorage state
    const currentUrl = this.getCurrentUrl();
    const urlMatch = currentUrl.match(/\/patient-overview\/([^\/]+)/);
    if (urlMatch && urlMatch[1]) {
      const savedState = this.localStorageService.getPatientOverviewState(urlMatch[1]);
      if (savedState) {
        this.localStorageService.savePatientOverviewState(urlMatch[1], {
          ...savedState,
          shareDirection: null
        });
      }
    }

    this.forceWizardStepForEdit(3);
  }

  private forceWizardStepForEdit(step: 2 | 3): void {
    const currentUrl = this.getCurrentUrl();
    const urlMatch = currentUrl.match(/\/patient-overview\/([^\/]+)/);
    const patientID = urlMatch?.[1];
    const facilityID = this._selectedTargetFacility?.facility;

    if (!patientID || !facilityID) {
      return;
    }

    let targetUrl = '';
    if (step === 2) {
      // Step 2 is type selection (DOCUMENT/IMAGE) - URL should include /type
      targetUrl = `/patient-overview/${patientID}/exchange/${facilityID}/type`;
    } else if (step === 3) {
      // Step 3 is SEND/RECEIVE selection - URL should include /images
      targetUrl = `/patient-overview/${patientID}/exchange/${facilityID}/images`;
    }

    // IMPORTANT: do not call onPageChange here because it has a /images guard that prevents
    // going back to earlier steps. For edit actions we intentionally allow navigating back.
    this.facade.currentPage = step;
    (this.vm as any).currentPage = step;
    if (targetUrl && targetUrl !== currentUrl) {
      this.location.go(targetUrl);
    }
    this.cdr.detectChanges();
  }

  onRestartSharingSetup(): void {
    this.facade.onRestartSharingSetup();

    // Clear local selected target facility
    this._selectedTargetFacility = null;

    // Reset to decision screen (like old component)
    if ((this.vm as any).showDecisionScreen !== undefined) {
      (this.vm as any).showDecisionScreen = true;
    }
    if ((this.vm as any).showSharingSetup !== undefined) {
      (this.vm as any).showSharingSetup = false;
    }

    // Clear localStorage state
    const currentUrl = this.router.url;
    const urlMatch = currentUrl.match(/\/patient-overview\/([^\/]+)/);
    if (urlMatch && urlMatch[1]) {
      this.localStorageService.clearPatientOverviewState(urlMatch[1]);
    }

    // Reset URL back to patient overview beginning (remove /exchange path)
    if (urlMatch && urlMatch[1]) {
      const patientID = urlMatch[1];
      this.router.navigateByUrl(`/patient-overview/${patientID}`);
    }

    this.cdr.detectChanges();
  }

  onIframeLoaded(): void {
    this.facade.onIframeLoaded();

    // Cookie-based authentication - no need to inject token via postMessage
    // The cookie is already set and will be sent automatically with requests
  }

  onIframeError(): void {
    console.error('[Patient Overview] Iframe failed to load');
    // Note: The facade may not have iframeError property, so we'll handle it in the template
    if ((this.facade as any).iframeError !== undefined) {
      (this.facade as any).iframeError = 'Failed to load the viewer. Please check your connection and try again.';
    }
    if ((this.facade as any).isIframeLoading !== undefined) {
      (this.facade as any).isIframeLoading = false;
    }
  }

  get activeImageShareTitle(): string {
    return this.facade.activeImageShareTitle;
  }

  get vm(): PatientOverviewFacade {
    return this.facade;
  }

  get isNarrowLink(): boolean {
    const userFacility = ((this.authService.userData.value as any)?.facility || '').toLowerCase();
    const linkedFacilities = this.vm.patient?.linkedFacilities;
    return !!(userFacility && linkedFacilities && linkedFacilities[userFacility]);
  }

  /** True when the logged-in user is a Spoke (facility-scoped) user. Spoke users may only Retrieve — never Send. */
  get isSpokeUser(): boolean {
    return (this.authService.userData.value as any)?.plancode === UserPlans.spoke;
  }

  get sourceFacility(): FacilityInfo | undefined {
    if (!(this.vm as any).currentFacilityPatientId) {
      return undefined;
    }
    const facilityName = this.vm.currentFacility || 'Current Facility';
    return {
      name: facilityName,
      patientId: (this.vm as any).currentFacilityPatientId.value
    };
  }

  get destinationFacility(): FacilityInfo | undefined {
    if (!this.selectedTargetFacility) {
      return undefined;
    }
    return {
      name: this.selectedTargetFacility.facility,
      patientId: this.selectedTargetFacility.value
    };
  }

  get facilitators(): FacilitatorInfo[] {
    // No user selection anymore; return empty array for instructions components
    return [];
  }

  get exchangeMode(): 'send' | 'receive' | null {
    return this.shareDirection;
  }

  get shouldShowSendInstructions(): boolean {
    // Only show in fullscreen mode
    if (!this.vm.isImageShareMode) {
      return false;
    }

    // Check if URL matches the pattern: /patient-overview/<patientId>/exchange/<targetFacilityId>/images/send
    const currentUrl = this.router.url;
    const urlPattern = /^\/patient-overview\/[^\/]+\/exchange\/[^\/]+\/images\/send$/;
    return urlPattern.test(currentUrl);
  }

  get shouldShowRetrieveInstructions(): boolean {
    // Only show in fullscreen mode
    if (!this.vm.isImageShareMode) {
      return false;
    }

    // Check if URL route ends in /retreive
    const currentUrl = this.router.url;
    return currentUrl.endsWith('/retreive');
  }

  // Getters for facade properties that may not exist on the base facade
  get selectedTargetFacility(): SharingPatientId | null {
    // First check local storage, then facade (for backward compatibility)
    if (this._selectedTargetFacility) {
      return this._selectedTargetFacility;
    }
    const facility = (this.vm as any).selectedTargetFacility;
    return facility || null;
  }

  /**
   * Format facility name for display (convert dashes to spaces and capitalize words)
   */
  getFormattedFacilityName(facilityName: string): string {
    if (!facilityName) return '';
    return facilityName
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  get currentFacilityName(): string {
    const facilityId = this.vm.currentFacilityPatientId?.facility || this.vm.currentFacility || '';
    if (!facilityId) return '';
    // Try to find in facilities array first
    const normalized = facilityId.toLowerCase().trim();
    const match = (this.vm.facilities || []).find(f => (f?.id || '').toLowerCase().trim() === normalized);
    if (match?.name) return match.name;
    // Fallback to formatting the ID
    return this.getFormattedFacilityName(facilityId);
  }

  get targetFacilityName(): string {
    const facilityId = this.selectedTargetFacility?.facility;
    if (!facilityId) return '';
    // Try to find in facilities array first
    const normalized = facilityId.toLowerCase().trim();
    const match = (this.vm.facilities || []).find(f => (f?.id || '').toLowerCase().trim() === normalized);
    if (match?.name) return match.name;
    // Fallback to formatting the ID
    return this.getFormattedFacilityName(facilityId);
  }

  get selectedFacilityUsers(): any[] {
    return []; // No user selection anymore; kept for compatibility with code that still reads this
  }

  get shareDirection(): 'send' | 'receive' | null {
    return (this.vm as any).shareDirection || null;
  }

  get facilityPacsUrl(): string | null {
    return (this.authService.userData.value as any)?.facilityPACSUrl || null;
  }

  get showDecisionScreen(): boolean {
    if (this.isNarrowLink) return false;
    // If facade has showDecisionScreen, use it
    if ((this.vm as any).showDecisionScreen !== undefined) {
      return (this.vm as any).showDecisionScreen;
    }
    // Default: show decision screen if we're not in exchange flow and not in image share mode
    const currentUrl = this.router.url;
    const isInExchangeFlow = currentUrl.includes('/exchange');
    return !isInExchangeFlow && !this.vm.isImageShareMode;
  }

  get showSharingSetup(): boolean {
    if (this.isNarrowLink) return false;
    // If facade has showSharingSetup, use it
    if ((this.vm as any).showSharingSetup !== undefined) {
      return (this.vm as any).showSharingSetup;
    }
    // Default: show sharing setup if we're in exchange flow or decision screen is hidden
    const currentUrl = this.router.url;
    const isInExchangeFlow = currentUrl.includes('/exchange');
    return isInExchangeFlow || !this.showDecisionScreen;
  }

  get activeImageShareTab(): 'pacs' | 'nuage' | 'orthanc' | 'studies' | null {
    // CRITICAL: If URL includes /images/send or /images/retreive, ALWAYS return 'studies'
    const currentUrl = this.router.url;
    if (currentUrl.includes('/images/send') || currentUrl.includes('/images/retreive')) {
      return 'studies';
    }

    // If forceStudiesTab flag is set, always return 'studies'
    if (this._forceStudiesTab) {
      return 'studies';
    }
    return (this.vm as any).activeImageShareTab || this.vm.activeImageShareTab || null;
  }

  get iframeError(): string | null {
    return (this.vm as any).iframeError || null;
  }

  get optionSelected(): string | null {
    return (this.vm as any).optionSelected || null;
  }

  get optionSelectedTitle(): string {
    if ((this.vm as any).getOptionSelectedTitle) {
      return (this.vm as any).getOptionSelectedTitle();
    }
    return '';
  }

  get formattedPatientNameForSearch(): string | null {
    if (!this.vm.patient) {
      return null;
    }
    const lastName = this.vm.patient.lastname?.trim() || '';
    const firstName = this.vm.patient.firstname?.trim() || '';

    if (!lastName && !firstName) {
      return null;
    }

    // Format as DICOM: LastName^FirstName
    return `${lastName}^${firstName}`;
  }

  get orthancUrl(): string {
    if ((this.vm as any).getOrthanC_URL) {
      return (this.vm as any).getOrthanC_URL();
    }
    return '';
  }

  /**
   * Setup visibility change listener to check for pending BtPO flag when tab becomes active
   * This handles the case where the event is dispatched before the component is ready
   */
  private setupVisibilityChangeListener(): void {
    const handleVisibilityChange = () => {
      // Check if tab became visible
      if (!document.hidden) {
        // Check for pending flag with a small delay to ensure localStorage is accessible
        setTimeout(() => {
          this.consumePendingBtpoFlag();
          this.consumePendingExternalCancelFlag();

          // After checking flag, only restore studies page state if URL already indicates it
          // Do NOT auto-navigate - let the user stay on whatever page they were on
          const freshUrl = this.router.url || this.getCurrentUrl();

          // Only ensure studies page is shown if URL already includes /images/send or /images/retreive
          // This restores the state without forcing navigation
          if (freshUrl.includes('/images/send') || freshUrl.includes('/images/retreive')) {
            this.ensureStudiesPageIsShown();
          }
        }, 100);
      }
    };

    // Listen for visibility change events
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Also listen for window focus events
    window.addEventListener('focus', handleVisibilityChange);

    // Clean up on destroy
    this.destroy$.subscribe(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    });
  }

  /**
   * Setup event listener for Chrome extension messages
   * Listens for "Back to Patient Overview" button click from extension
   */
  private setupExtensionEventListener(): void {
    // Store the handler so we can remove it later
    this.backToPatientOverviewHandler = (event: Event) => {
      const customEvent = event as CustomEvent;
      const eventTimestamp = customEvent.detail?.timestamp || Date.now();

      // Prevent concurrent processing
      if (this.isProcessingBtpo) {
        // Force reset if it's been stuck for more than 5 seconds
        const timeSinceLastCall = Date.now() - this.lastBtpoEventTimestamp;
        if (timeSinceLastCall > 5000) {
          this.isProcessingBtpo = false;
          // Continue processing below
        } else {
          return;
        }
      }

      // Debounce: ignore if called within the debounce window
      const timeSinceLastCall = Date.now() - this.lastBtpoEventTimestamp;
      if (timeSinceLastCall < this.BTPO_DEBOUNCE_MS && !this.isProcessingBtpo) {
        return;
      }

      // Update timestamp but DON'T set isProcessingBtpo here - let the deferred function handle it
      this.lastBtpoEventTimestamp = Date.now();

      // Defer the handler to avoid running during CD / constructor timing.
      this.deferBackToPatientOverviewFromExtension();
    };

    this.externalCancelHandler = (event: Event) => {
      this.onExternalCancelFromExtension();
    };

    // Listen only on window (events bubble, so this catches all dispatches)
    window.addEventListener('nuagedx-back-to-patient-overview', this.backToPatientOverviewHandler);
    window.addEventListener('nuagedx-external-cancelled', this.externalCancelHandler);

    // If BtPO happened before this component mounted, consume the one-shot flag now.
    this.consumePendingBtpoFlag();
    this.consumePendingExternalCancelFlag();
  }

  /**
   * Ensure studies page is shown when URL includes /images/send or /images/retreive
   * This is a fallback to ensure the studies page is visible even if other state checks fail
   */
  private ensureStudiesPageIsShown(): void {
    if (this._documentExchangeActive) return;
    const currentUrl = this.router.url || this.getCurrentUrl();
    if (!currentUrl.includes('/images/send') && !currentUrl.includes('/images/retreive')) {
      return;
    }

    // Restore selectedTargetFacility from localStorage if it was wiped by resetState().
    // Without this, the SOURCE→TARGET animation disappears after facade re-initialization.
    if (!this._selectedTargetFacility) {
      const patientIdMatch = currentUrl.match(/\/patient-overview\/([^\/]+)/);
      if (patientIdMatch?.[1]) {
        const saved = this.localStorageService.getPatientOverviewState(patientIdMatch[1]);
        if (saved?.selectedTargetFacility) {
          this._selectedTargetFacility = saved.selectedTargetFacility;
        }
      }
    }

    // Set state to show studies page
    this.vm.isImageShareMode = true;
    this.vm.isIframeLoading = false;
    this._forceStudiesTab = true;
    (this.vm as any).activeImageShareTab = 'studies';
    if ((this.facade as any).activeImageShareTab !== undefined) {
      (this.facade as any).activeImageShareTab = 'studies';
    }

    // Ensure shareTypeSelected is 'image'
    if (this.vm.shareTypeSelected !== 'image') {
      this.vm.shareTypeSelected = 'image';
    }
    this.facade.shareTypeSelected = 'image';

    // Set shareDirection based on URL
    if ((this.vm as any).shareDirection === undefined || (this.vm as any).shareDirection === null) {
      (this.vm as any).shareDirection = currentUrl.includes('/send') ? 'send' : 'receive';
    }

    // Clear iframe error if any
    if ((this.vm as any).iframeError !== undefined) {
      (this.vm as any).iframeError = null;
    }

    // Force change detection
    this.cdr.markForCheck();
    this.cdr.detectChanges();

  }

  /**
   * Resolve the Hub facility from the URL segment `/exchange/:facilityID` and store it as
   * `_selectedTargetFacility`. Must be called AFTER `facade.initialize()` resolves so that
   * `vm.facilities` (Orthanc modalities) is populated.
   *
   * This is a targeted fix for the race condition where `initializeFromUrl()` fires via
   * `setTimeout(0)` before the `Promise.all([getModalities(), getFacilities()])` inside
   * `facade.initialize()` completes — leaving `vm.facilities` empty and the facility lookup
   * silently failing.
   */
  private restoreFacilityFromUrl(url: string): void {
    if (this._selectedTargetFacility) return; // already resolved

    const facilityMatch = url.match(/\/exchange\/([^\/]+)/);
    const facilityIdFromUrl = facilityMatch?.[1];
    if (!facilityIdFromUrl || facilityIdFromUrl === 'pending') return;

    const patientIdMatch = url.match(/\/patient-overview\/([^\/]+)/);
    const patientID = patientIdMatch?.[1];

    const normalized = facilityIdFromUrl.toLowerCase().replace(/-/g, '');

    // 1. Prefer otherPatientIDs — the patient may already have an MRN at the Hub
    let match: SharingPatientId | undefined = (this.vm.otherPatientIDs || []).find(
      (pid: SharingPatientId) => pid.facility?.toLowerCase().replace(/-/g, '') === normalized
    );

    // 2. Fall back to vm.facilities (Orthanc modalities enriched with Firestore display names)
    if (!match) {
      const facilityObj = (this.vm.facilities || []).find(
        (f: Facility) => f.id?.toLowerCase().replace(/-/g, '') === normalized
      );
      if (facilityObj) {
        match = { facility: facilityObj.id, value: '', ownerId: '' };
      }
    }

    if (match) {
      this._selectedTargetFacility = match;
      if (patientID) {
        this.localStorageService.updatePatientOverviewState(patientID, {
          selectedTargetFacility: match
        });
      }
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }
  }

  /**
   * Consume the BtPO one-shot flag that the extension stores in localStorage.
   * This makes the behavior robust to timing (event fired before component mounted).
   */
  private consumePendingBtpoFlag(): void {
    try {
      const raw = localStorage.getItem('nuagedx_btpo_pending');
      if (!raw) {
        // Even if no flag, check if URL requires studies page
        const currentUrl = this.router.url || this.getCurrentUrl();
        if (currentUrl.includes('/images/send') || currentUrl.includes('/images/retreive')) {
          // Defer so we don't run change detection during constructor (avoids "Should be run in update mode")
          setTimeout(() => this.ensureStudiesPageIsShown(), 0);
        }
        // Don't return here - let the calling code handle navigation if needed
        return;
      }

      let payload: any = null;
      try {
        payload = JSON.parse(raw);
      } catch {
        payload = { raw };
      }

      // Guard against stale triggers (e.g., from a previous session)
      const ts = typeof payload?.timestamp === 'number' ? payload.timestamp : 0;
      const ageMs = ts ? Date.now() - ts : Number.POSITIVE_INFINITY;

      // Only consume flags that are very recent (within 5 seconds) to avoid stale flags
      if (ageMs > 5 * 1000) {
        // Flag is too old, clear it and ignore
        localStorage.removeItem('nuagedx_btpo_pending');
        console.warn('[Patient Overview] BtPO flag found but too old; ignoring and clearing.', { payload, ageMs });
        return;
      }

      // Check if this flag was already consumed in this session
      const consumedKey = `nuagedx_btpo_consumed_${ts}`;
      const alreadyConsumed = sessionStorage.getItem(consumedKey);

      if (alreadyConsumed) {
        // Already consumed in this session, clear the flag and ignore
        localStorage.removeItem('nuagedx_btpo_pending');
        console.warn('[Patient Overview] BtPO flag already consumed in this session; ignoring.', { payload, ageMs });
        return;
      }

      // Clean up old sessionStorage entries (older than 1 hour)
      try {
        const oneHourAgo = Date.now() - 60 * 60 * 1000;
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && key.startsWith('nuagedx_btpo_consumed_')) {
            const timestamp = parseInt(key.replace('nuagedx_btpo_consumed_', ''));
            if (timestamp && timestamp < oneHourAgo) {
              sessionStorage.removeItem(key);
            }
          }
        }
      } catch (e) {
        // Ignore cleanup errors
      }

      // Mark as consumed in this session
      sessionStorage.setItem(consumedKey, 'true');

      // Clear the flag immediately to ensure one-shot behavior
      localStorage.removeItem('nuagedx_btpo_pending');

      // Defer handling so we don't run during construction / initial CD pass.
      this.deferBackToPatientOverviewFromExtension();
    } catch (e) {
      console.warn('[Patient Overview] Could not consume BtPO flag from localStorage:', e);
      // Clear the flag on error to prevent it from sticking
      try {
        localStorage.removeItem('nuagedx_btpo_pending');
      } catch {
        // Ignore
      }
    }
  }

  /**
   * Consume the external cancel one-shot flag that the extension stores in localStorage.
   */
  private consumePendingExternalCancelFlag(): void {
    try {
      const raw = localStorage.getItem('nuagedx_cancel_pending');
      if (!raw) {
        return;
      }

      let payload: any = null;
      try {
        payload = JSON.parse(raw);
      } catch {
        payload = { raw };
      }

      const ts = typeof payload?.timestamp === 'number' ? payload.timestamp : 0;
      const ageMs = ts ? Date.now() - ts : Number.POSITIVE_INFINITY;

      if (ageMs > 10 * 1000) {
        localStorage.removeItem('nuagedx_cancel_pending');
        console.warn('[Patient Overview] Cancel flag too old; ignoring and clearing.', { payload, ageMs });
        return;
      }

      const consumedKey = `nuagedx_cancel_consumed_${ts}`;
      const alreadyConsumed = sessionStorage.getItem(consumedKey);
      if (alreadyConsumed) {
        localStorage.removeItem('nuagedx_cancel_pending');
        return;
      }

      sessionStorage.setItem(consumedKey, 'true');
      localStorage.removeItem('nuagedx_cancel_pending');

      this.onExternalCancelFromExtension();
    } catch (e) {
      console.warn('[Patient Overview] Could not consume cancel flag from localStorage:', e);
      try {
        localStorage.removeItem('nuagedx_cancel_pending');
      } catch {
        // Ignore
      }
    }
  }

  private getCurrentUrl(): string {
    // We use Location.go() for wizard step transitions to avoid router navigation (and component re-creation).
    // Because of that, Router.url can be stale; Location.path() is the source of truth while the wizard runs.
    const p = this.location.path();
    if (!p) return this.router.url;
    return p.startsWith('/') ? p : `/${p}`;
  }

  /**
   * Handle cancel event from Chrome extension (external PACS cancel button).
   */
  private onExternalCancelFromExtension(): void {
    this.uiMessaging_$.toastMessage('The process has been finished by the user.', 'OK');
    this.onRestartSharingSetup();
  }

  /**
   * Handle "Back to Patient Overview" event from Chrome extension
   * This method is called when the "Back to Patient Overview" button is clicked in the external app
   */
  onBackToPatientOverviewFromExtension(): void {
    // Get patient ID and facility ID from current URL or component data
    const currentUrl = this.router.url;
    let patientID: string | null = null;
    let facilityID: string | null = null;

    // Try to extract from current URL first
    const urlMatch = currentUrl.match(/\/patient-overview\/([^\/]+)/);
    if (urlMatch && urlMatch[1]) {
      patientID = urlMatch[1];
    }

    const facilityMatch = currentUrl.match(/\/exchange\/([^\/]+)/);
    if (facilityMatch && facilityMatch[1]) {
      facilityID = facilityMatch[1];
    }

    // Fallback to component data if not in URL
    if (!patientID && this.vm.patient?.caseName) {
      patientID = this.vm.patient.caseName;
    } else if (!patientID && this.vm.patient?.patientId) {
      patientID = this.vm.patient.patientId;
    }

    if (!facilityID && this.selectedTargetFacility?.facility) {
      facilityID = this.selectedTargetFacility.facility;
    }

    // If we still don't have both, try to get from saved state
    let savedState: any = null;
    if (patientID) {
      savedState = this.localStorageService.getPatientOverviewState(patientID);
      if (!facilityID && savedState?.selectedTargetFacility?.facility) {
        facilityID = savedState.selectedTargetFacility.facility;
      }

      // CRITICAL: Restore selectedTargetFacility from localStorage
      // This ensures the facility animation is displayed (like RETRIEVE flow)
      if (savedState?.selectedTargetFacility) {
        this._selectedTargetFacility = savedState.selectedTargetFacility;
      }
    }

    // Set up fullscreen mode with studies page FIRST (before navigation)
    // This ensures the state is set before initializeFromUrl() runs
    const orthancUrlMethod = (this.vm as any).getOrthanC_URL;
    const orthancUrl = orthancUrlMethod ? orthancUrlMethod() : this.orthancUrl;

    // Set up fullscreen mode with studies page (like RETRIEVE flow)
    // This will show: studies page on right, animation on left, facility users below
    this.vm.isImageShareMode = true;
    // Studies page is a component, not an iframe, so no loading state needed
    this.vm.isIframeLoading = false;
    if ((this.vm as any).iframeError !== undefined) {
      (this.vm as any).iframeError = null;
    }

    // CRITICAL: Set activeImageShareTab to 'studies' directly (bypassing TypeScript type check)
    // The facade's type doesn't include 'studies', but we need it for the studies-page component
    this._forceStudiesTab = true; // Set flag to preserve studies tab
    (this.vm as any).activeImageShareTab = 'studies';
    // Also set on facade directly if it exists
    if ((this.facade as any).activeImageShareTab !== undefined) {
      (this.facade as any).activeImageShareTab = 'studies';
    }

    // Ensure share type is set to image (required for fullscreen)
    if (this.vm.shareTypeSelected !== 'image') {
      this.vm.shareTypeSelected = 'image';
    }

    // Set share direction to 'send'
    if ((this.vm as any).shareDirection !== undefined) {
      (this.vm as any).shareDirection = 'send';
    }

    // Make dialog fullscreen (if dialog exists)
    if (this.dialogRef) {
      // Angular Material 12 doesn't have addPanelClass, updatePosition, or updateSize
      // Use _containerInstance to access the DOM directly
      if (this.dialogRef._containerInstance) {
        const panelElement = (this.dialogRef._containerInstance as any)._elementRef?.nativeElement;
        if (panelElement) {
          panelElement.classList.add('fullscreen-dialog');
          // Set position and size via CSS
          panelElement.style.position = 'fixed';
          panelElement.style.top = '0px';
          panelElement.style.left = '0px';
          panelElement.style.right = '0px';
          panelElement.style.bottom = '0px';
          panelElement.style.width = '100vw';
          panelElement.style.height = '100vh';
          panelElement.style.maxWidth = '100vw';
          panelElement.style.maxHeight = '100vh';
        }
      }
    }

    // Navigate to the URL with /send at the end (without "-modal")
    // Do this AFTER setting the state so initializeFromUrl() doesn't override it
    if (patientID && facilityID) {
      const targetUrl = `/patient-overview/${patientID}/exchange/${facilityID}/images/send`;

      // Set isNavigatingInternally flag to prevent route subscriptions from interfering
      this.isNavigatingInternally = true;

      this.router.navigateByUrl(targetUrl).then(() => {
        // CRITICAL: Ensure state is set IMMEDIATELY after navigation completes
        // This must happen before any other code runs
        this.vm.isImageShareMode = true;
        this.vm.isIframeLoading = false;
        this._forceStudiesTab = true;
        (this.vm as any).activeImageShareTab = 'studies';
        if ((this.facade as any).activeImageShareTab !== undefined) {
          (this.facade as any).activeImageShareTab = 'studies';
        }

        // Set shareDirection to 'send' for the studies page
        if ((this.vm as any).shareDirection !== undefined) {
          (this.vm as any).shareDirection = 'send';
        }

        // Ensure shareTypeSelected is 'image' for fullscreen mode
        this.vm.shareTypeSelected = 'image';
        this.facade.shareTypeSelected = 'image';

        // Re-apply selectedTargetFacility after navigation
        if (savedState?.selectedTargetFacility) {
          this._selectedTargetFacility = savedState.selectedTargetFacility;
        }

        // Clear iframe error if any
        if ((this.vm as any).iframeError !== undefined) {
          (this.vm as any).iframeError = null;
        }

        // Force change detection immediately - do this BEFORE any async operations
        this.cdr.markForCheck();
        this.cdr.detectChanges();

        // Use requestAnimationFrame to ensure state is set after all Angular lifecycle hooks
        requestAnimationFrame(() => {
          // Re-apply state again to ensure it persists
          this._forceStudiesTab = true;
          (this.vm as any).activeImageShareTab = 'studies';
          if ((this.facade as any).activeImageShareTab !== undefined) {
            (this.facade as any).activeImageShareTab = 'studies';
          }
          this.vm.isImageShareMode = true;
          this.vm.isIframeLoading = false;

          if ((this.vm as any).shareDirection !== undefined) {
            (this.vm as any).shareDirection = 'send';
          }

          if (this.vm.shareTypeSelected !== 'image') {
            this.vm.shareTypeSelected = 'image';
          }
          if (this.facade.shareTypeSelected !== 'image') {
            this.facade.shareTypeSelected = 'image';
          }

          this.cdr.markForCheck();
          this.cdr.detectChanges();

          // Also use setTimeout as a fallback
          setTimeout(() => {
            this._forceStudiesTab = true;
            (this.vm as any).activeImageShareTab = 'studies';
            if ((this.facade as any).activeImageShareTab !== undefined) {
              (this.facade as any).activeImageShareTab = 'studies';
            }
            this.vm.isImageShareMode = true;
            this.vm.isIframeLoading = false;

            // Force multiple change detection cycles to ensure template updates
            this.cdr.markForCheck();
            this.cdr.detectChanges();

            // Additional change detection after a brief delay
            setTimeout(() => {
              this.cdr.markForCheck();
              this.cdr.detectChanges();
            }, 50);

            // Reset navigation flag after a delay
            setTimeout(() => {
              this.isNavigatingInternally = false;
            }, 200);
          }, 150);
        });

      }).catch((error) => {
        console.error('[Patient Overview] Navigation error:', error);
        this.isNavigatingInternally = false;
      });
    } else {
      console.warn('[Patient Overview] Cannot navigate: missing patientID or facilityID', { patientID, facilityID });
    }

    // Trigger change detection safely (BtPO can fire during navigation/initialization).
    this.safeDetectChanges('BtPO immediate');
  }

  /**
   * Defer BtPO handling to avoid Angular "update mode" assertion errors.
   * BtPO can be triggered while the component is being constructed (via pending flag),
   * so we schedule the actual work to run after the current call stack.
   */
  private deferBackToPatientOverviewFromExtension(): void {
    // Debounce: if multiple triggers happen quickly, only process once.
    if (this.btpoDeferredTimer) {
      clearTimeout(this.btpoDeferredTimer);
    }

    this.btpoDeferredTimer = setTimeout(() => {
      // Respect existing debounce/processing guards
      const now = Date.now();
      const timeSinceLastCall = now - this.lastBtpoEventTimestamp;

      // Check debounce window (but allow if processing flag was force-reset)
      if (timeSinceLastCall < this.BTPO_DEBOUNCE_MS && this.isProcessingBtpo) {
        return;
      }

      // Set processing flag here, not in the event handler
      this.isProcessingBtpo = true;
      this.lastBtpoEventTimestamp = now;

      try {
        this.onBackToPatientOverviewFromExtension();
      } catch (error) {
        console.error('[Patient Overview] Error in onBackToPatientOverviewFromExtension:', error);
        // Reset flag on error
        this.isProcessingBtpo = false;
      } finally {
        // Reset processing flag after a longer delay to allow for navigation + async state
        // Increased from 250ms to 2000ms to ensure navigation completes
        setTimeout(() => {
          this.isProcessingBtpo = false;
        }, 2000);
      }
    }, 0);
  }

  /**
   * Avoids "Should be run in update mode" by deferring detectChanges().
   */
  private safeDetectChanges(reason: string): void {
    // markForCheck is always safe
    try {
      this.cdr.markForCheck();
    } catch {
      // ignore
    }

    setTimeout(() => {
      try {
        this.cdr.detectChanges();
      } catch (e) {
        // Don't let a CD timing issue break the BtPO flow
        console.warn('[Patient Overview] safeDetectChanges failed:', reason, e);
      }
    }, 0);
  }

  /**
   * Initialize component state from URL path and localStorage
   */
  private initializeFromUrl(): void {
    // Prevent duplicate calls
    if (this.isInitializingFromUrl) {
      return;
    }

    this.isInitializingFromUrl = true;
    try {
      // Don't initialize if we don't have patient data yet - this prevents triggering reloads
      // Check both vm.patient and data.patient since vm might not be updated immediately after facade.initialize()
      const hasPatientData =
        (this.vm.patient && this.vm.patient.caseName) || (this.data?.patient && this.data.patient.caseName);
      if (!hasPatientData) {
        return;
      }

      const currentUrl = this.router.url;
      const urlMatch = currentUrl.match(/\/patient-overview\/([^\/]+)/);

      if (!urlMatch || !urlMatch[1]) {
        // If no patient ID in URL, ensure decision screen is shown
        if ((this.vm as any).showDecisionScreen !== undefined) {
          (this.vm as any).showDecisionScreen = true;
        }
        if ((this.vm as any).showSharingSetup !== undefined) {
          (this.vm as any).showSharingSetup = false;
        }
        return;
      }

      const patientID = urlMatch[1];
      const savedState = this.localStorageService.getPatientOverviewState(patientID);

      // Restore state based on URL path
      if (currentUrl.includes('/exchange')) {
        // In exchange flow: hide decision screen, show sharing setup
        if ((this.vm as any).showDecisionScreen !== undefined) {
          (this.vm as any).showDecisionScreen = false;
        }
        if ((this.vm as any).showSharingSetup !== undefined) {
          (this.vm as any).showSharingSetup = true;
        }

        // CRITICAL: Restore facility FIRST before determining page
        // This ensures selectedTargetFacility is set before the template renders
        // Restore facility if in URL - try route params first, then URL match
        const facilityID = this.route.snapshot.paramMap.get('facilityID') || this.route.snapshot.params['facilityID'];

        if (facilityID || currentUrl.includes('/exchange/')) {
          const facilityIdFromUrl =
            facilityID ||
            (() => {
              const facilityMatch = currentUrl.match(/\/exchange\/([^\/]+)/);
              return facilityMatch && facilityMatch[1] ? facilityMatch[1] : null;
            })();

          if (facilityIdFromUrl) {
            // Normalize facility ID for matching (handle various formats)
            const normalizedFacilityId = facilityIdFromUrl.toLowerCase().trim();

            // CRITICAL: Check if _selectedTargetFacility is already set and matches the URL
            // If so, preserve it to avoid race conditions with onTargetFacilitySelected()
            if (this._selectedTargetFacility) {
              const currentFacilityId = this._selectedTargetFacility.facility?.toLowerCase().trim();
              if (currentFacilityId === normalizedFacilityId ||
                currentFacilityId?.replace(/-/g, '') === normalizedFacilityId.replace(/-/g, '')) {
                // Still mark for check to ensure Angular detects it
                this.cdr.markForCheck();
                // Skip restoration logic since we already have the correct value
              } else {
                // Clear it so restoration logic can run
                this._selectedTargetFacility = null;
              }
            }

            // Try to restore from saved state first (this is the fastest path)
            if (!this._selectedTargetFacility && savedState?.selectedTargetFacility) {
              // Verify the saved facility matches the URL facility ID
              const savedFacilityId = savedState.selectedTargetFacility.facility?.toLowerCase().trim();
              if (savedFacilityId === normalizedFacilityId ||
                savedFacilityId?.replace(/-/g, '') === normalizedFacilityId.replace(/-/g, '')) {
                // Store locally for immediate access - CRITICAL: Do this synchronously
                this._selectedTargetFacility = savedState.selectedTargetFacility;
                // Mark for check to ensure Angular detects the change
                this.cdr.markForCheck();
              } else {
                console.warn('PatientOverviewV2Component: Saved facility does not match URL facility ID', {
                  savedFacilityId,
                  normalizedFacilityId,
                  savedFacility: savedState.selectedTargetFacility
                });
                // Fall through to matching logic below
              }
            }

            // If not restored from saved state, try to construct from facilityID
            if (!this._selectedTargetFacility) {
              // First, try to find in otherPatientIDs (these are the facilities the patient has IDs for)
              let matchingPatientId = this.vm.otherPatientIDs.find(
                (pid: SharingPatientId) => {
                  const pidFacility = pid.facility?.toLowerCase().trim() || '';
                  return pidFacility === normalizedFacilityId ||
                    pidFacility.replace(/-/g, '') === normalizedFacilityId.replace(/-/g, '') ||
                    pidFacility.replace(/\s+/g, '-') === normalizedFacilityId;
                }
              );

              // If not found in otherPatientIDs, try to find in facilities list
              if (!matchingPatientId && this.vm.facilities && this.vm.facilities.length > 0) {
                const matchingFacility = this.vm.facilities.find(
                  (facility: any) => {
                    const facilityId = facility.id?.toLowerCase().trim() || '';
                    const facilityName = facility.name?.toLowerCase().trim().replace(/\s+/g, '-') || '';
                    return facilityId === normalizedFacilityId ||
                      facilityId.replace(/-/g, '') === normalizedFacilityId.replace(/-/g, '') ||
                      facilityName === normalizedFacilityId ||
                      facilityName.replace(/-/g, '') === normalizedFacilityId.replace(/-/g, '');
                  }
                );

                if (matchingFacility) {
                  // Construct a SharingPatientId from the facility
                  // Try to find the actual patient ID for this facility in otherPatientIDs
                  const actualPatientId = this.vm.otherPatientIDs.find(
                    (pid: SharingPatientId) => pid.facility?.toLowerCase().trim() === matchingFacility.id?.toLowerCase().trim()
                  );

                  matchingPatientId = actualPatientId || {
                    facility: matchingFacility.id,
                    value: '', // Will be set when we find the actual patient ID
                    ownerId: ''
                  };
                }
              }

              if (matchingPatientId) {
                // Store locally for immediate access
                this._selectedTargetFacility = matchingPatientId;
                // Save to localStorage so it persists
                this.localStorageService.updatePatientOverviewState(patientID, {
                  selectedTargetFacility: matchingPatientId
                });
                // Trigger change detection to update the UI
                this.cdr.detectChanges();
              } else {
                console.warn('PatientOverviewV2Component: Could not find facility in otherPatientIDs or facilities list:', {
                  facilityIdFromUrl: normalizedFacilityId,
                  otherPatientIDs: this.vm.otherPatientIDs?.map((pid: SharingPatientId) => pid.facility),
                  facilities: this.vm.facilities?.map((f: any) => ({ id: f.id, name: f.name }))
                });
              }
            }
          }
        }

        // IMPORTANT: Facility restoration happens above, so _selectedTargetFacility should be set by now
        // Wait a tick to ensure facility restoration is complete before determining page
        // This ensures the Continue button appears when facility is selected

        // Set the correct page based on URL structure (source of truth)
        // URL structure determines the page (NEW FLOW):
        // - /exchange/<facilityID> -> Page 1 (facility selection)
        // - /exchange/<facilityID>/type -> Page 2 (exchange type selection - moved from page 3)
        // - /exchange/<facilityID>/images -> Page 3 (SEND/RECEIVE selection - moved from page 4)
        // - /exchange/<facilityID>/images/send -> Page 4 (users selection - moved from page 2, only for image + send)
        let targetPage = 1; // Default to step 1

        // Check if we're in the exchange flow (with or without facilityID)
        const isExchangeFlow = currentUrl.match(/\/exchange\/[^\/]+/);

        if (isExchangeFlow) {
          // Determine page based on URL structure (most reliable)
          if (currentUrl.includes('/images/send')) {
            // URL includes /images/send -> Page 4 (users selection for image + send)
            targetPage = 4;
          } else if (currentUrl.includes('/images')) {
            // URL includes /images -> Page 3 (SEND/RECEIVE selection)
            targetPage = 3;
          } else if (currentUrl.includes('/type')) {
            // URL includes /type -> Page 2 (exchange type selection - moved from page 3)
            // CRITICAL: Always set to 2 for /type URL, regardless of current state
            targetPage = 2;
          } else {
            // URL is /exchange/<facilityID> -> Determine page based on completed steps
            const hasTargetFacility = this._selectedTargetFacility || savedState?.selectedTargetFacility;

            if (hasTargetFacility) {
              targetPage = 2;
            } else {
              // No facility selected yet, stay on step 1
              targetPage = 1;
            }
          }

          // CRITICAL: Set shareTypeSelected based on URL structure BEFORE setting page
          // This prevents the facade's clampCurrentPageToAllowed() from resetting the page
          if (currentUrl.includes('/images') && !this._documentExchangeActive) {
            // URL has /images -> shareTypeSelected must be 'image'
            if ((this.vm as any).shareTypeSelected !== undefined) {
              (this.vm as any).shareTypeSelected = 'image';
            }
            this.facade.shareTypeSelected = 'image';

            // CRITICAL: Only set isImageShareMode to true if we're past the setup phase
            // Check if URL includes /send or /retreive (indicating direction is selected)
            // OR if we're already in fullscreen mode
            // Don't set it to true if we're still on page 3 (SEND/RECEIVE selection)
            const isPastSetupPhase = currentUrl.includes('/images/send') ||
              currentUrl.includes('/images/retreive') ||
              (this.vm as any).shareDirection !== null;

            if (isPastSetupPhase) {
              // We're past the setup phase - should be in fullscreen mode with studies page
              // This handles the case when coming back from the external app via "Back to Patient Overview"
              this.vm.isImageShareMode = true;
              this.vm.isIframeLoading = false; // Studies page is a component, not an iframe
              if ((this.vm as any).iframeError !== undefined) {
                (this.vm as any).iframeError = null;
              }

              // Set activeImageShareTab to 'studies' to show the studies-page component
              // ALWAYS force studies tab when URL includes /images - this is required for the component to show
              this._forceStudiesTab = true; // Use flag to preserve studies tab
              (this.vm as any).activeImageShareTab = 'studies';
              if ((this.facade as any).activeImageShareTab !== undefined) {
                (this.facade as any).activeImageShareTab = 'studies';
              }
            } else {
              // We're still in setup phase (page 3 - SEND/RECEIVE selection)
              // Keep isImageShareMode false so the SEND/RECEIVE buttons are visible
              this.vm.isImageShareMode = false;
            }

            // Set shareDirection based on URL (send or retreive)
            if (currentUrl.includes('/send')) {
              if ((this.vm as any).shareDirection !== undefined) {
                (this.vm as any).shareDirection = 'send';
              }
              if (savedState) {
                this.localStorageService.updatePatientOverviewState(patientID, {
                  shareDirection: 'send'
                });
              }
            } else if (currentUrl.includes('/retreive')) {
              if ((this.vm as any).shareDirection !== undefined) {
                (this.vm as any).shareDirection = 'receive';
              }
              if (savedState) {
                this.localStorageService.updatePatientOverviewState(patientID, {
                  shareDirection: 'receive'
                });
              }
            } else if (savedState?.shareDirection) {
              // Restore from savedState if URL doesn't specify
              if ((this.vm as any).shareDirection !== undefined) {
                (this.vm as any).shareDirection = savedState.shareDirection;
              }
            }

            // CRITICAL: Trigger change detection to ensure template updates
            this.cdr.markForCheck();
            this.cdr.detectChanges();

            // Also ensure state persists after a brief delay (in case of async operations)
            setTimeout(() => {
              // Re-assert state to ensure it wasn't reset
              // Use fresh URL check (not closure variable) to avoid stale state
              const freshUrl = this.router.url || this.getCurrentUrl();
              const isPastSetup = freshUrl.includes('/images/send') || freshUrl.includes('/images/retreive');

              if (freshUrl.includes('/images') && isPastSetup) {
                this.vm.isImageShareMode = true;
                this._forceStudiesTab = true;
                (this.vm as any).activeImageShareTab = 'studies';
                if ((this.facade as any).activeImageShareTab !== undefined) {
                  (this.facade as any).activeImageShareTab = 'studies';
                }
                // Ensure shareDirection is set
                if ((this.vm as any).shareDirection === undefined || (this.vm as any).shareDirection === null) {
                  (this.vm as any).shareDirection = freshUrl.includes('/send') ? 'send' : 'receive';
                }
                this.cdr.markForCheck();
                this.cdr.detectChanges();
              }
            }, 100);

          } else if (currentUrl.includes('/type') && savedState?.shareTypeSelected) {
            // URL has /type and we have saved shareTypeSelected -> restore it
            if ((this.vm as any).shareTypeSelected !== undefined) {
              (this.vm as any).shareTypeSelected = savedState.shareTypeSelected;
            }
            this.facade.shareTypeSelected = savedState.shareTypeSelected;
          }

          // Set the page on both vm and facade
          // CRITICAL: Always set vm.currentPage, even if it's undefined initially
          // This ensures the template binding works correctly
          // CRITICAL: For /type URL, we MUST set page to 2 regardless of facade state
          if (currentUrl.includes('/type')) {
            (this.vm as any).currentPage = 2;
            this.facade.currentPage = 2;
            this.cdr.markForCheck();
            this.cdr.detectChanges();
          } else {
            (this.vm as any).currentPage = targetPage;
            this.facade.currentPage = targetPage;
          }

          if (currentUrl.includes('/type')) {
            setTimeout(() => {
              if ((this.vm as any).currentPage !== 2 || this.facade.currentPage !== 2) {
                (this.vm as any).currentPage = 2;
                this.facade.currentPage = 2;
                this.cdr.detectChanges();
              }
            }, 0);
            setTimeout(() => {
              if ((this.vm as any).currentPage !== 2 || this.facade.currentPage !== 2) {
                (this.vm as any).currentPage = 2;
                this.facade.currentPage = 2;
                this.cdr.detectChanges();
              }
            }, 100);
          }

          // CRITICAL: If URL includes /images, ensure state is set and trigger change detection
          if (currentUrl.includes('/images')) {
            // Force change detection after all state is set
            this.cdr.markForCheck();
            this.cdr.detectChanges();

            // Additional change detection after initialization completes
            setTimeout(() => {
              this.cdr.markForCheck();
              this.cdr.detectChanges();
            }, 200);
          }

          // CRITICAL: Trigger change detection after setting page and restoring facility
          // This ensures the Continue button appears when facility is selected
          this.cdr.detectChanges();

          // FINAL SAFEGUARD: If we're on /exchange/<facilityID> with a facility selected, ensure page is 2
          // This handles the auto-advance case where a facility is selected
          if (currentUrl.includes('/exchange/') && !currentUrl.includes('/type') && !currentUrl.includes('/images')) {
            const hasTargetFacility = this._selectedTargetFacility || savedState?.selectedTargetFacility;
            if (hasTargetFacility && targetPage === 2) {
              // Re-assert page 2 after a short delay to catch any late resets
              setTimeout(() => {
                if ((this.vm as any).currentPage !== 2 || this.facade.currentPage !== 2) {
                  (this.vm as any).currentPage = 2;
                  this.facade.currentPage = 2;
                  this.cdr.detectChanges();
                }
              }, 0);
            }
          }

          // FINAL SAFEGUARD: If we're on /type URL with facility selected, ensure page is 2
          if (currentUrl.includes('/type')) {
            const hasFacility = !!this._selectedTargetFacility;
            if (hasFacility) {
              setTimeout(() => {
                const finalPage = (this.vm as any).currentPage || this.facade.currentPage;
                if (finalPage !== 2) {
                  (this.vm as any).currentPage = 2;
                  this.facade.currentPage = 2;
                  this.cdr.markForCheck();
                  this.cdr.detectChanges();
                }
              }, 200);
            }
          }
        }

        // Restore direction ONLY if we're on step 4 (all previous steps completed)
        // This prevents jumping to step 4 when only target facility is selected
        if (targetPage === 4) {
          if (currentUrl.includes('/retreive')) {
            // URL has /retreive -> restore receive direction
            if ((this.vm as any).shareDirection !== undefined) {
              (this.vm as any).shareDirection = 'receive';
            }
            if (typeof (this.vm as any).onReceiveSelected === 'function') {
              (this.vm as any).onReceiveSelected();
            }
          } else if (currentUrl.includes('/send')) {
            // URL has /send -> restore send direction
            if ((this.vm as any).shareDirection !== undefined) {
              (this.vm as any).shareDirection = 'send';
            }
          } else if (savedState?.shareDirection) {
            // Restore from saved state
            if (savedState.shareDirection === 'send') {
              if ((this.vm as any).shareDirection !== undefined) {
                (this.vm as any).shareDirection = 'send';
              }
              if (typeof (this.vm as any).onSendSelected === 'function') {
                (this.vm as any).onSendSelected();
              }
            } else {
              if ((this.vm as any).shareDirection !== undefined) {
                (this.vm as any).shareDirection = 'receive';
              }
              if (typeof (this.vm as any).onReceiveSelected === 'function') {
                (this.vm as any).onReceiveSelected();
              }
            }
          }
        }
      } else if (currentUrl.includes('/send-documents') || currentUrl.includes('/retrieve-documents')) {
        // Restore document exchange state from URL
        const docDir: 'send' | 'receive' = currentUrl.includes('/send-documents') ? 'send' : 'receive';
        this._documentExchangeActive = true;
        this.vm.shareTypeSelected = 'document';
        this.facade.shareTypeSelected = 'document';
        if ((this.vm as any).shareDirection !== undefined) {
          (this.vm as any).shareDirection = docDir;
        }
        this.vm.isImageShareMode = true;
        if ((this.vm as any).showDecisionScreen !== undefined) {
          (this.vm as any).showDecisionScreen = false;
        }
        if ((this.vm as any).showSharingSetup !== undefined) {
          (this.vm as any).showSharingSetup = false;
        }
      } else {
        // Not in exchange flow: show decision screen, hide sharing setup
        // This ensures the decision screen is shown when first loading the patient overview
        if ((this.vm as any).showDecisionScreen !== undefined) {
          (this.vm as any).showDecisionScreen = true;
        }
        if ((this.vm as any).showSharingSetup !== undefined) {
          (this.vm as any).showSharingSetup = false;
        }
      }

      // CRITICAL: Ensure change detection runs after all state restoration
      // This ensures the UI updates correctly, especially the Continue button visibility
      // Use setTimeout to avoid "Should be run in update mode" error
      setTimeout(() => {
        // Use the safe CD helper (also avoids update-mode assertion errors)
        this.safeDetectChanges('initializeFromUrl completed');
      }, 0);
    } finally {
      // Always reset the in-progress flag, even if we returned early above.
      this.isInitializingFromUrl = false;
    }
  }

  onTargetFacilitySelected(patientId: SharingPatientId): void {
    // Store locally for immediate access
    this._selectedTargetFacility = patientId;


    // CRITICAL: Clear any old state that might cause getMaxAllowedStep() to return a higher value
    // Clear shareTypeSelected and shareDirection to ensure we start from step 2
    // Do this BEFORE calling facade methods to prevent incorrect page clamping
    if ((this.vm as any).shareTypeSelected !== undefined) {
      (this.vm as any).shareTypeSelected = null;
    }
    if ((this.vm as any).shareDirection !== undefined) {
      (this.vm as any).shareDirection = null;
    }

    // Try to call facade method if it exists
    if (typeof (this.vm as any).onTargetFacilitySelected === 'function') {
      (this.vm as any).onTargetFacilitySelected(patientId);
    }

    // Save to localStorage - also clear old state to prevent jumping ahead
    const currentUrl = this.getCurrentUrl();
    const urlMatch = currentUrl.match(/\/patient-overview\/([^\/]+)/);
    if (urlMatch && urlMatch[1]) {
      this.localStorageService.updatePatientOverviewState(urlMatch[1], {
        selectedTargetFacility: patientId,
        shareTypeSelected: null, // Clear share type
        shareDirection: null // Clear direction
      });
    }

    // Update URL when target facility is selected
    if (urlMatch && urlMatch[1] && patientId.facility) {
      const patientID = urlMatch[1];
      const facilityID = patientId.facility;
      const targetUrl = `/patient-overview/${patientID}/exchange/${facilityID}`;

      // Update URL WITHOUT router navigation (prevents component re-creation / "reload")
      if (targetUrl !== currentUrl) {
        this.location.go(targetUrl);
      }
    }

    // Auto-advance to step 2 immediately (no Continue click needed)
    this.facade.currentPage = 2;
    (this.vm as any).currentPage = 2;

    // Trigger change detection to update the title bar
    this.cdr.detectChanges();
  }

  onShareTypeSelected(shareType: 'document' | 'image'): void {
    // Set shareTypeSelected on the facade/vm first so onPageChange can see it
    if ((this.vm as any).shareTypeSelected !== undefined) {
      (this.vm as any).shareTypeSelected = shareType;
    }
    this.facade.shareTypeSelected = shareType;

    // Try to call facade method if it exists
    if (typeof (this.vm as any).onShareTypeSelected === 'function') {
      (this.vm as any).onShareTypeSelected(shareType);
    }

    // Save to localStorage BEFORE navigation to ensure state is available when initializeFromUrl() runs
    const currentUrl = this.getCurrentUrl();
    const urlMatch = currentUrl.match(/\/patient-overview\/([^\/]+)/);
    if (urlMatch && urlMatch[1]) {
      // Get existing state and merge with new shareTypeSelected
      const existingState = this.localStorageService.getPatientOverviewState(urlMatch[1]) || {};
      this.localStorageService.updatePatientOverviewState(urlMatch[1], {
        ...existingState,
        shareTypeSelected: shareType
      });
    }

    // AUTO-ADVANCE:
    // Clicking "IMAGE" on step 2 should immediately advance to step 3 (SEND/RECEIVE).
    // IMPORTANT: If we leave currentPage at 2 while setting shareTypeSelected='image',
    // the facade may clamp the page back down. So we always drive the transition via onPageChange(3),
    // which also updates the URL to /images using Location.go().
    if (shareType === 'image') {
      this.onPageChange(3);
      return;
    }

    if (shareType === 'document') {
      // For document share, handle it via facade
      this.onDocumentShare();
      return;
    }
  }

  generateSharingImageLink(caseName: string, senderFacilityName: string): string {
    return `${environment.config.host}patient-overview/${caseName}/sender/${senderFacilityName}/retreive/images`;
  }

  // NOTE: This only happens with the following flow:
  // 1. User selects a target facility
  // 2. User selects IMAGES (type of exchange)
  // 3. User clicks on the "Send" button
  // 4. User selects facility users (if image + send)
  onSendSelected(): void {
    // Set shareDirection first
    if ((this.vm as any).shareDirection !== undefined) {
      (this.vm as any).shareDirection = 'send';
    }

    // Save to localStorage
    const currentUrl = this.router.url;
    const urlMatch = currentUrl.match(/\/patient-overview\/([^\/]+)/);
    if (urlMatch && urlMatch[1]) {
      this.localStorageService.updatePatientOverviewState(urlMatch[1], {
        shareDirection: 'send'
      });
    }

    // For image + send, navigate to page 4 (users selection)
    // The email preparation will happen after users are selected
    if (this.vm.shareTypeSelected === 'image') {
      this.onPageChange(4);
      return;
    }

    // For document share or other flows, proceed with existing logic (no user selection anymore)
    if (this.selectedTargetFacility?.facility) {
      /**
       * Prepare the data to share the patient with the target facility
       * No destination emails when no users are selected; studies-page may handle notification separately
       */
      this.mailer_$.sendImagesNotificationEmail = {
        destinationEmails: [],
        action: 'shared-image-access',
        messageData: {
          targetEmail: '',
          caseName: this.vm.patient.caseName,
          files: this.vm.patient.files,
          extraMessage: this.vm.patient.extraMessage,
          link: this.generateSharingImageLink(this.vm.patient.caseName, this.vm.currentFacility)
        }
      };

      this.firebaseUtilities_$.dataToSharingPatientWithFacility = {
        casename: this.vm.patient.caseName,
        facility: this.selectedTargetFacility.facility,
        sharerFacility: this.vm.currentFacility
      };
    }

    // Try to call facade method if it exists
    if (typeof (this.vm as any).onSendSelected === 'function') {
      (this.vm as any).onSendSelected();
    }
  }

  /**
   * Complete the image send flow after target facility is selected
   * This method prepares and sends the email notification (no user selection anymore)
   */
  async completeImageSend(): Promise<void> {
    if (!this.selectedTargetFacility?.facility) {
      console.warn('Cannot complete image send: no target facility selected');
      return;
    }

    this.mailer_$.sendImagesNotificationEmail = {
      destinationEmails: [],
      action: 'shared-image-access',
      messageData: {
        targetEmail: '',
        caseName: this.vm.patient.caseName,
        files: this.vm.patient.files,
        extraMessage: this.vm.patient.extraMessage,
        link: this.generateSharingImageLink(this.vm.patient.caseName, this.vm.currentFacility)
      }
    };

    this.firebaseUtilities_$.dataToSharingPatientWithFacility = {
      casename: this.vm.patient.caseName,
      facility: this.selectedTargetFacility.facility,
      sharerFacility: this.vm.currentFacility
    };

    // Look up the target facility's PACS URL from Firestore (optional — skip if not configured)
    const facilityId = this.selectedTargetFacility.facility;
    const pacsUrl = await this.authService.getFacilityPacsURL(facilityId);
    if (pacsUrl) {
      const enhancedPatientData = this.sharingSetupComponent?.getEnhancedPatientData();
      this.openExternalUrlWithExtension(pacsUrl, enhancedPatientData);
    }
  }

  /**
   * Opens an external URL with Chrome extension detection.
   * If the extension is installed, opens via extension with patient data.
   * Otherwise, falls back to opening in a new browser tab.
   *
   * HIPAA COMPLIANCE NOTE:
   * - PHI is NOT logged to console
   * - PHI is NOT stored in sessionStorage
   * - PHI is NOT passed via URL query parameters
   * - Only non-sensitive identifiers are transmitted via secure Chrome extension messaging
   *
   * @param url The URL to open
   * @param patientData The patient data to send with the URL
   */
  private openExternalUrlWithExtension(url: string, patientData: any): void {
    const chrome = (window as any).chrome;

    // Check if Chrome extension API is available
    if (!chrome || !chrome.runtime || !chrome.runtime.sendMessage) {
      // HIPAA: Do not log URL or patient data
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    // Extension ID for Medical NuageDx Navigation Helper
    const EXTENSION_ID = 'ljnabhknhhbjdhagcmakbnpeoifimnhn';

    // Prepare patient data - extract only non-sensitive identifiers
    // HIPAA: Do NOT include sensitive PHI fields (firstname, lastname, dob, gender, placeOfBirth, mrn, ssn, clientname)
    let dataToSend = null;
    if (patientData) {
      dataToSend = {
        patientId: patientData.patientId,
        patientIDs: patientData.patientIDs,
        caseName: patientData.caseName,
        ownerID: patientData.ownerID,
        // Include sharing setup if available (contains facility IDs and user selections, not PHI)
        sharingSetup: patientData.sharingSetup || null
      };
    }

    // HIPAA COMPLIANCE: Do NOT log patient data to console
    // HIPAA COMPLIANCE: Do NOT store PHI in sessionStorage
    // HIPAA COMPLIANCE: Do NOT pass PHI via URL query parameters

    // Try to send message to extension
    try {
      chrome.runtime.sendMessage(
        EXTENSION_ID,
        {
          type: 'OPEN_EXTERNAL',
          url: url,
          patientData: dataToSend
        },
        (response: any) => {
          if (chrome.runtime.lastError) {
            // Extension not accessible - open URL without patient data
            // HIPAA: Never pass PHI via URL parameters
            window.open(url, '_blank', 'noopener,noreferrer');
            return;
          }

          if (response && response.ok) {
            // Extension handled it successfully
          } else {
            // Fallback: open URL without patient data
            // HIPAA: Never pass PHI via URL parameters
            window.open(url, '_blank', 'noopener,noreferrer');
          }
        }
      );
    } catch (error) {
      // HIPAA: Do not log error details that might contain PHI
      // Fallback: open URL without patient data
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  async onReceiveSelected(): Promise<void> {
    // CRITICAL: Navigate to /retreive FIRST before calling facade.onReceiveSelected()
    // This ensures the pathname includes /retreive when goToFullscreenMode() stores the route
    const currentUrl = this.router.url;
    const urlMatch2 = currentUrl.match(/\/patient-overview\/([^\/]+)\/exchange\/([^\/]+)\/images/);
    if (urlMatch2 && urlMatch2[1] && urlMatch2[2]) {
      const patientID = urlMatch2[1];
      const facilityID = urlMatch2[2];
      // Navigate to /retreive route FIRST
      await this.router.navigateByUrl(`/patient-overview/${patientID}/exchange/${facilityID}/images/retreive`);
      // Wait for navigation to complete and pathname to update
      await new Promise(resolve => setTimeout(resolve, 10));
    }

    // Set shareDirection on facade/vm (like old component)
    if ((this.vm as any).shareDirection !== undefined) {
      (this.vm as any).shareDirection = 'receive';
    }

    // Save to localStorage
    const urlMatch = currentUrl.match(/\/patient-overview\/([^\/]+)/);
    if (urlMatch && urlMatch[1]) {
      this.localStorageService.updatePatientOverviewState(urlMatch[1], {
        shareDirection: 'receive'
      });
    }

    // Replicate old facade's onReceiveSelected() logic
    // The new facade doesn't have this method, so we implement it here
    // Automatically go to fullscreen view based on share type
    if (this.vm.shareTypeSelected === 'image') {
      // Direct access to studies page for 'receive' (Retrieve)
      // Replicate old facade's goToFullscreenMode logic for 'studies' tab
      this.vm.isImageShareMode = true;
      this.vm.isIframeLoading = false; // Studies page is a component, not an iframe

      // Set activeImageShareTab to 'studies' for receive
      if ((this.vm as any).activeImageShareTab !== undefined) {
        (this.vm as any).activeImageShareTab = 'studies';
      }

      // Clear iframe error
      if ((this.vm as any).iframeError !== undefined) {
        (this.vm as any).iframeError = null;
      }

      // Make dialog fullscreen (if dialog exists)
      if (this.dialogRef) {
        // Angular Material 12 doesn't have addPanelClass, updatePosition, or updateSize
        // Use _containerInstance to access the DOM directly
        if (this.dialogRef._containerInstance) {
          const panelElement = (this.dialogRef._containerInstance as any)._elementRef?.nativeElement;
          if (panelElement) {
            panelElement.classList.add('fullscreen-dialog');
            // Set position and size via CSS
            panelElement.style.position = 'fixed';
            panelElement.style.top = '0px';
            panelElement.style.left = '0px';
            panelElement.style.right = '0px';
            panelElement.style.bottom = '0px';
            panelElement.style.width = '100vw';
            panelElement.style.height = '100vh';
            panelElement.style.maxWidth = '100vw';
            panelElement.style.maxHeight = '100vh';
          }
        }
      }

    } else if (this.vm.shareTypeSelected === 'document') {
      // For document share, open the client profile
      await this.onDocumentShare();
    }

    // Trigger change detection
    this.cdr.detectChanges();
  }

  onPatientsActionsDecisionSelected(option: 'images-sending' | 'images-retrieving' | 'documents-sending' | 'documents-retrieving'): void {
    const slugMap: Record<typeof option, string> = {
      'images-sending': 'send-images',
      'images-retrieving': 'retrieve-images',
      'documents-sending': 'send-documents',
      'documents-retrieving': 'retrieve-documents'
    };
    const patientID =
      this.router.url.match(/\/patient-overview\/([^\/]+)/)?.[1] ||
      this.vm.patient?.caseName ||
      this.vm.patient?.patientId;
    if (patientID) {
      this.location.replaceState(`/patient-overview/${patientID}/${slugMap[option]}`);
    }
  }

  onPatientsActionsOpenClientProfile(): void {
    this.handleDocumentsExchange();
  }

  onPatientsActionsOpenPacs(): void {
    const pacsUrl = this.facilityPacsUrl;
    if (!pacsUrl) return;

    const patient = this.vm.patient as any;
    const patientPayload = patient ? {
      patientId: patient.patientId || patient.caseName,
      patientIDs: patient.patientIDs,
      caseName: patient.caseName,
      ownerID: patient.ownerID || patient.uidOfCreator,
      firstname: patient.firstname || patient.FirstName,
      lastname: patient.lastname || patient.LastName,
      dob: patient.dob || patient.DateOfBirth || patient.dateOfBirth,
      sharingSetup: {
        currentFacility: this.vm.currentFacility || null,
        targetFacility: this._selectedTargetFacility ? {
          facility: this._selectedTargetFacility.facility,
          value: this._selectedTargetFacility.value
        } : null
      }
    } : null;

    const extensionEvent = new CustomEvent('nuagedx-open-external', {
      detail: { url: pacsUrl, patientData: patientPayload },
      cancelable: true
    });
    const notHandledByExtension = document.dispatchEvent(extensionEvent);
    if (notHandledByExtension) {
      window.open(pacsUrl, '_blank', 'noopener,noreferrer');
    }
  }

  onPatientsActionsFacilitySelected(patientId: SharingPatientId): void {
    this.onTargetFacilitySelected(patientId);
  }

  onPatientsActionsContinue(option: 'images-sending' | 'images-retrieving' | 'documents-sending' | 'documents-retrieving'): void {
    if (option === 'images-sending') {
      this.handleImagesSending();
    } else if (option === 'images-retrieving') {
      this.handleImagesRetrieving();
    } else if (option === 'documents-sending' || option === 'documents-retrieving') {
      const direction: 'send' | 'receive' = option === 'documents-sending' ? 'send' : 'receive';
      // Use the same image-share zone as the breadcrumb document switch
      this._documentExchangeActive = true;
      this.vm.shareTypeSelected = 'document';
      this.facade.shareTypeSelected = 'document';
      if ((this.vm as any).shareDirection !== undefined) {
        (this.vm as any).shareDirection = direction;
      }
      this.vm.isImageShareMode = true;
      if ((this.vm as any).showDecisionScreen !== undefined) {
        (this.vm as any).showDecisionScreen = false;
      }
      // URL was already set to /send-documents or /retrieve-documents by onPatientsActionsDecisionSelected
      this.cdr.detectChanges();
    }
  }

  onPatientsActionsReset(): void {
    // Clear document component state
    this.activeDocumentComponent = null;
    this.onRestartSharingSetup();
  }

  onDecisionSelected(option: 'profile' | 'library' | 'exchange' | 'images-sending' | 'images-retrieving' | 'documents-exchange'): void {
    // CRITICAL: Clear all preselected data when a decision button is clicked
    // This ensures a fresh start when the user makes a new decision
    // Clear target facility
    this._selectedTargetFacility = null;

    // Clear share type and direction
    if ((this.vm as any).shareTypeSelected !== undefined) {
      (this.vm as any).shareTypeSelected = null;
    }
    this.facade.shareTypeSelected = null;

    if ((this.vm as any).shareDirection !== undefined) {
      (this.vm as any).shareDirection = null;
    }

    // Clear localStorage state for the patient
    const currentUrl = this.router.url;
    const urlMatch = currentUrl.match(/\/patient-overview\/([^\/]+)/);
    if (urlMatch && urlMatch[1]) {
      const patientID = urlMatch[1];
      this.localStorageService.clearPatientOverviewState(patientID);
    } else if (this.vm.patient?.caseName) {
      // Fallback: use caseName if URL parsing fails
      this.localStorageService.clearPatientOverviewState(this.vm.patient.caseName);
    }

    // Reset page to 1 (start of wizard)
    if ((this.vm as any).currentPage !== undefined) {
      (this.vm as any).currentPage = 1;
    }
    this.facade.currentPage = 1;

    // Set optionSelected (like old component)
    if ((this.vm as any).optionSelected !== undefined) {
      (this.vm as any).optionSelected = option;
    }

    // Try to call facade method if it exists
    if (typeof (this.vm as any).onDecisionSelected === 'function') {
      (this.vm as any).onDecisionSelected(option);
    }

    // Handle "Images Sending" - load studies page and open PACS
    if (option === 'images-sending') {
      this.handleImagesSending();
      return;
    }

    // Handle "Images Retrieving" - load studies page in right zone
    if (option === 'images-retrieving') {
      this.handleImagesRetrieving();
      return;
    }

    // Handle "Documents Exchange" - open Client Profile
    if (option === 'documents-exchange') {
      this.handleDocumentsExchange();
      return;
    }

    // Update URL when "Images and Documents Exchange" option is selected
    if (option === 'exchange') {
      // Hide decision screen and show sharing setup
      if ((this.vm as any).showDecisionScreen !== undefined) {
        (this.vm as any).showDecisionScreen = false;
      }
      if ((this.vm as any).showSharingSetup !== undefined) {
        (this.vm as any).showSharingSetup = true;
      }

      const currentUrl = this.router.url;
      // Extract patientID from current URL (format: /patient-overview/<patientID>)
      const urlMatch = currentUrl.match(/\/patient-overview\/([^\/]+)/);
      if (urlMatch && urlMatch[1]) {
        const patientID = urlMatch[1];
        this.router.navigateByUrl(`/patient-overview/${patientID}/exchange`);
      } else if (this.vm.patient?.caseName) {
        // Fallback: use caseName from patient data if URL parsing fails
        this.router.navigateByUrl(`/patient-overview/${this.vm.patient.caseName}/exchange`);
      } else if (this.vm.patient?.patientId) {
        // Fallback: use patientId from patient data if URL parsing fails
        this.router.navigateByUrl(`/patient-overview/${this.vm.patient.patientId}/exchange`);
      }
    }
    // Options 1 and 2 (profile and library) are disabled for now (like old component)

    this.cdr.detectChanges();
  }

  /**
   * Handle "Images Sending" button click:
   * 1. Load the studies-page component in the right zone
   * 2. Open the external PACS system in a new window
   */
  private handleImagesSending(): void {
    // Get patient ID from URL or patient data
    const currentUrl = this.router.url;
    const urlMatch = currentUrl.match(/\/patient-overview\/([^\/]+)/);
    let patientID: string | null = null;

    if (urlMatch && urlMatch[1]) {
      patientID = urlMatch[1];
    } else if (this.vm.patient?.caseName) {
      patientID = this.vm.patient.caseName;
    } else if (this.vm.patient?.patientId) {
      patientID = this.vm.patient.patientId;
    }

    if (!patientID) {
      console.error('[Patient Overview] handleImagesSending() - No patient ID found');
      return;
    }

    // Set state BEFORE navigation to prevent redirect to facility selection
    // Hide decision screen and show sharing setup
    if ((this.vm as any).showDecisionScreen !== undefined) {
      (this.vm as any).showDecisionScreen = false;
    }
    if ((this.vm as any).showSharingSetup !== undefined) {
      (this.vm as any).showSharingSetup = true;
    }

    // Set share type to 'image' and direction to 'send'
    this.vm.shareTypeSelected = 'image';
    this.facade.shareTypeSelected = 'image';
    (this.vm as any).shareDirection = 'send';

    // Set page to 4 BEFORE navigation to bypass facility selection (page 1)
    // This prevents the sharing setup component from redirecting to page 1
    if ((this.vm as any).currentPage !== undefined) {
      (this.vm as any).currentPage = 4;
    }
    this.facade.currentPage = 4;

    // Set state to show studies page BEFORE navigation
    this.vm.isImageShareMode = true;
    this.vm.isIframeLoading = false;
    this._forceStudiesTab = true;
    (this.vm as any).activeImageShareTab = 'studies';
    if ((this.facade as any).activeImageShareTab !== undefined) {
      (this.facade as any).activeImageShareTab = 'studies';
    }

    // Clear iframe error if any
    if ((this.vm as any).iframeError !== undefined) {
      (this.vm as any).iframeError = null;
    }

    // Navigate to URL that includes /images/send to trigger studies page
    // Use selected facility ID if available, otherwise fallback to placeholder
    const targetFacilityId = this._selectedTargetFacility?.facility || 'pending';
    const targetUrl = `/patient-overview/${patientID}/exchange/${targetFacilityId}/images/send`;

    this.router.navigateByUrl(targetUrl).then(() => {
      // Use setTimeout with a small delay to ensure state is set after Angular's change detection cycle
      // This prevents the sharing setup component from resetting the page
      // The delay allows the route params subscription to run first
      setTimeout(() => {
        // Ensure studies page is shown (this method checks URL and sets up state)
        this.ensureStudiesPageIsShown();

        // Force page 4 again after navigation to ensure it sticks
        // Do this multiple times to override any resets from sharing setup component
        if ((this.vm as any).currentPage !== undefined) {
          (this.vm as any).currentPage = 4;
        }
        this.facade.currentPage = 4;

        // Ensure all state is set correctly
        this.vm.isImageShareMode = true;
        this.vm.isIframeLoading = false;
        this._forceStudiesTab = true;
        (this.vm as any).activeImageShareTab = 'studies';
        if ((this.facade as any).activeImageShareTab !== undefined) {
          (this.facade as any).activeImageShareTab = 'studies';
        }
        this.vm.shareTypeSelected = 'image';
        this.facade.shareTypeSelected = 'image';
        (this.vm as any).shareDirection = 'send';

        // Force change detection
        this.cdr.markForCheck();
        this.cdr.detectChanges();

        // Set page 4 one more time after change detection to ensure it sticks
        setTimeout(() => {
          if ((this.vm as any).currentPage !== undefined) {
            (this.vm as any).currentPage = 4;
          }
          this.facade.currentPage = 4;
          this.cdr.detectChanges();
        }, 50);

      }, 100);
    }).catch((error) => {
      console.error('[Patient Overview] handleImagesSending() - Navigation error:', error);
    });

  }

  /**
   * Handle "Images Retrieving" button click:
   * Load the studies-page component in the right zone
   */
  private handleImagesRetrieving(): void {
    // Get patient ID from URL or patient data
    const currentUrl = this.router.url;
    const urlMatch = currentUrl.match(/\/patient-overview\/([^\/]+)/);
    let patientID: string | null = null;

    if (urlMatch && urlMatch[1]) {
      patientID = urlMatch[1];
    } else if (this.vm.patient?.caseName) {
      patientID = this.vm.patient.caseName;
    } else if (this.vm.patient?.patientId) {
      patientID = this.vm.patient.patientId;
    }

    if (!patientID) {
      console.error('[Patient Overview] handleImagesRetrieving() - No patient ID found');
      return;
    }

    // Restore selectedTargetFacility from localStorage if not already set.
    // Retrieve Images skips facility selection, so without this the animation
    // SOURCE → TARGET never shows on first use.
    if (!this._selectedTargetFacility) {
      const savedState = this.localStorageService.getPatientOverviewState(patientID);
      if (savedState?.selectedTargetFacility) {
        this._selectedTargetFacility = savedState.selectedTargetFacility;
      }
    }

    // Set state BEFORE navigation to prevent redirect to facility selection
    // Hide decision screen and show sharing setup
    if ((this.vm as any).showDecisionScreen !== undefined) {
      (this.vm as any).showDecisionScreen = false;
    }
    if ((this.vm as any).showSharingSetup !== undefined) {
      (this.vm as any).showSharingSetup = true;
    }

    // Set share type to 'image' and direction to 'receive'
    this.vm.shareTypeSelected = 'image';
    this.facade.shareTypeSelected = 'image';
    (this.vm as any).shareDirection = 'receive';

    // Set page to 4 BEFORE navigation to bypass facility selection (page 1)
    // This prevents the sharing setup component from redirecting to page 1
    if ((this.vm as any).currentPage !== undefined) {
      (this.vm as any).currentPage = 4;
    }
    this.facade.currentPage = 4;

    // Set state to show studies page BEFORE navigation
    this.vm.isImageShareMode = true;
    this.vm.isIframeLoading = false;
    this._forceStudiesTab = true;
    (this.vm as any).activeImageShareTab = 'studies';
    if ((this.facade as any).activeImageShareTab !== undefined) {
      (this.facade as any).activeImageShareTab = 'studies';
    }

    // Clear iframe error if any
    if ((this.vm as any).iframeError !== undefined) {
      (this.vm as any).iframeError = null;
    }

    // Navigate to URL that includes /images/retreive to trigger studies page
    // Use selected facility ID if available, otherwise fallback to placeholder
    // Note: The codebase uses 'retreive' (typo) instead of 'retrieve'
    const targetFacilityId = this._selectedTargetFacility?.facility || 'pending';
    const targetUrl = `/patient-overview/${patientID}/exchange/${targetFacilityId}/images/retreive`;

    this.router.navigateByUrl(targetUrl).then(() => {
      // Use setTimeout with a small delay to ensure state is set after Angular's change detection cycle
      // This prevents the sharing setup component from resetting the page
      // The delay allows the route params subscription to run first
      setTimeout(() => {
        // Ensure studies page is shown (this method checks URL and sets up state)
        this.ensureStudiesPageIsShown();

        // Force page 4 again after navigation to ensure it sticks
        // Do this multiple times to override any resets from sharing setup component
        if ((this.vm as any).currentPage !== undefined) {
          (this.vm as any).currentPage = 4;
        }
        this.facade.currentPage = 4;

        // Ensure all state is set correctly
        this.vm.isImageShareMode = true;
        this.vm.isIframeLoading = false;
        this._forceStudiesTab = true;
        (this.vm as any).activeImageShareTab = 'studies';
        if ((this.facade as any).activeImageShareTab !== undefined) {
          (this.facade as any).activeImageShareTab = 'studies';
        }
        this.vm.shareTypeSelected = 'image';
        this.facade.shareTypeSelected = 'image';
        (this.vm as any).shareDirection = 'receive';

        // Force change detection
        this.cdr.markForCheck();
        this.cdr.detectChanges();

        // Set page 4 one more time after change detection to ensure it sticks
        setTimeout(() => {
          if ((this.vm as any).currentPage !== undefined) {
            (this.vm as any).currentPage = 4;
          }
          this.facade.currentPage = 4;
          this.cdr.detectChanges();
        }, 50);

      }, 100);
    }).catch((error) => {
      console.error('[Patient Overview] handleImagesRetrieving() - Navigation error:', error);
    });
  }

  /**
   * Handle "Documents Exchange" button click:
   * Open the Client Profile
   */
  private async handleDocumentsExchange(): Promise<void> {
    // Set share type to 'document' so the client profile knows it's for document exchange
    this.vm.shareTypeSelected = 'document';
    this.facade.shareTypeSelected = 'document';

    // Open the client profile using the existing onDocumentShare method
    await this.onDocumentShare();

    this.cdr.detectChanges();
  }

   onBreadcrumbSwitchType(newType: 'image' | 'document'): void {
    const caseName = this.router.url.match(/\/patient-overview\/([^\/]+)/)?.[1] || this.vm.patient?.caseName;
    if (newType === 'document') {
      // Spoke users can only Retrieve — skip the direction dialog and force "receive"
      if (this.isSpokeUser) {
        this._documentExchangeActive = true;
        this.vm.shareTypeSelected = 'document';
        this.facade.shareTypeSelected = 'document';
        if ((this.vm as any).shareDirection !== undefined) {
          (this.vm as any).shareDirection = 'receive';
        }
        if (caseName) {
          this.location.go(`/patient-overview/${caseName}/retrieve-documents`);
        }
        this.cdr.detectChanges();
        return;
      }
      const ref = this.dialog.open(OptionChooserComponent, {
        width: '320px',
        data: {
          title: 'Documents Exchange',
          message: 'Choose the direction for document exchange.',
          mode: 'button',
          options: [
            { name: 'Send', value: 'send' },
            { name: 'Retrieve', value: 'receive' }
          ]
        }
      });
      ref.afterClosed().subscribe((direction: 'send' | 'receive' | undefined) => {
        if (!direction) return;
        this._documentExchangeActive = true;
        this.vm.shareTypeSelected = 'document';
        this.facade.shareTypeSelected = 'document';
        if ((this.vm as any).shareDirection !== undefined) {
          (this.vm as any).shareDirection = direction;
        }
        if (caseName) {
          this.location.go(`/patient-overview/${caseName}/${direction === 'send' ? 'send' : 'retrieve'}-documents`);
        }
        this.cdr.detectChanges();
      });
      return;
    }
    // Switching back to Images
    this._documentExchangeActive = false;
    // Spoke users can only Retrieve — no dialog needed
    if (this.isSpokeUser) {
      this.vm.shareTypeSelected = 'image';
      this.facade.shareTypeSelected = 'image';
      if ((this.vm as any).shareDirection !== undefined) {
        (this.vm as any).shareDirection = 'receive';
      }
      this._forceStudiesTab = true;
      (this.vm as any).activeImageShareTab = 'studies';
      if ((this.facade as any).activeImageShareTab !== undefined) {
        (this.facade as any).activeImageShareTab = 'studies';
      }
      if (caseName) {
        this.location.go(`/patient-overview/${caseName}/retrieve-images`);
      }
      this.cdr.detectChanges();
      return;
    }
    // Hub users: prompt for Send or Retrieve
    const ref = this.dialog.open(OptionChooserComponent, {
      width: '320px',
      data: {
        title: 'Images Exchange',
        message: 'Choose the direction for image exchange.',
        mode: 'button',
        options: [
          { name: 'Send', value: 'send' },
          { name: 'Retrieve', value: 'receive' }
        ]
      }
    });
    ref.afterClosed().subscribe((direction: 'send' | 'receive' | undefined) => {
      if (!direction) return;
      this.vm.shareTypeSelected = 'image';
      this.facade.shareTypeSelected = 'image';
      if ((this.vm as any).shareDirection !== undefined) {
        (this.vm as any).shareDirection = direction;
      }
      this._forceStudiesTab = true;
      (this.vm as any).activeImageShareTab = 'studies';
      if ((this.facade as any).activeImageShareTab !== undefined) {
        (this.facade as any).activeImageShareTab = 'studies';
      }
      if (caseName) {
        this.location.go(`/patient-overview/${caseName}/${direction === 'send' ? 'send' : 'retrieve'}-images`);
      }
      this.cdr.detectChanges();
    });
  }

  onBreadcrumbSwitchDirection(newDir: 'send' | 'receive'): void {
    // Spoke users may never send
    if (newDir === 'send' && this.isSpokeUser) return;
    if ((this.vm as any).shareDirection !== undefined) {
      (this.vm as any).shareDirection = newDir;
    }
    // Keep URL in sync when in document exchange mode
    if (this._documentExchangeActive) {
      const caseName = this.router.url.match(/\/patient-overview\/([^\/]+)/)?.[1] || this.vm.patient?.caseName;
      if (caseName) {
        this.location.go(`/patient-overview/${caseName}/${newDir === 'send' ? 'send' : 'retrieve'}-documents`);
      }
    }
    this.cdr.detectChanges();
  }

  onCreateFacilityUser(): void {
    const targetFacility = this.selectedTargetFacility;
    if (!targetFacility) {
      return;
    }

    // Get ownerID from patient data or auth service
    // Priority: patient.ownerID > patient.owners[0] > auth service ownerID
    let ownerID: string | undefined;

    if (this.vm.patient?.ownerID) {
      ownerID = this.vm.patient.ownerID;
    } else if (this.vm.patient?.owners && this.vm.patient.owners.length > 0) {
      ownerID = this.vm.patient.owners[0];
    }
    // If still no ownerID, it will be handled by CreateFacilityUserComponent's ngOnInit

    // The facility identifier should be the facility name (lowercased), not the patient ID
    // targetFacility.facility contains the facility name (e.g., "wade-wilson-hospital")
    // targetFacility.value contains the patient ID (e.g., "wwh-jb-001")

    const dialogRef = this.dialog.open(CreateFacilityUserComponent, {
      width: '600px',
      data: {
        title: 'Create Facility User',
        skipFacilitySelection: true,
        preSelectedFacility: {
          name: targetFacility.facility,
          id: targetFacility.facility.toLowerCase().replace(/\s+/g, '-'), // Use facility name as ID
          ownerID: ownerID // Include ownerID if available
        },
        role: 'admin', // Default to admin/facilitator
        limits: {
          limits: {
            limits: {
              users: {
                admin: 999,
                facility: 999,
                consultant: 999,
                associate: 999
              }
            },
            users: {
              admin: [],
              facility: [],
              consultant: [],
              associate: []
            }
          }
        }
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Add the newly created user to the SharingSetupComponent's user list
        if (this.sharingSetupComponent) {
          this.sharingSetupComponent.addNewUser({
            id: result.id || result.email, // Use id if available, otherwise email
            name: result.name,
            email: result.email,
            role: result.role === 'admin' ? 'Facilitator' : result.role,
            facility: result.facility
          });
        } else {
          console.warn('SharingSetupComponent not available to update user list');
        }
      }
    });
  }

  // DEV ONLY — remove before release
  async devOpenClientProfile() {
    const { ClientProfileComponent } = await import('../clientprofile/clientprofile.component');
    this.dialog.open(ClientProfileComponent, {
      width: '95%',
      minWidth: '95%',
      maxWidth: '95%',
      height: '90%',
      id: 'dev-client-profile',
      data: { patient: this.vm.patient }
    });
  }

  private loadPatientShares(): void {
    if (this.isSpokeUser) return;
    const caseName = this.vm.patient?.caseName;
    const hubFacilityId = (this.authService.userData.value as any)?.facility;
    if (!caseName || !hubFacilityId) return;

    this.studyShareService.getSharesForPatient(caseName, hubFacilityId)
      .then(shares => {
        const map = new Map<string, StudyShare>();
        for (const share of shares) {
          const fid = share.shared_to_facility_id;
          const existing = map.get(fid);
          if (!existing || share.shared_at?.toDate() > existing.shared_at?.toDate()) {
            map.set(fid, share);
          }
        }
        this.patientSharesByFacility = map;
        this.patientSharesLoaded = true;
        this.cdr.detectChanges();
      })
      .catch(err => {
        console.error('[patient-overview-v2] loadPatientShares failed:', err);
        this.patientSharesLoaded = true;
        this.cdr.detectChanges();
      });
  }

  getLatestShareForFacility(facilityId: string): StudyShare | null {
    if (!facilityId) return null;
    return this.patientSharesByFacility.get(facilityId.toLowerCase()) ?? null;
  }

  formatShareRelativeTime(share: StudyShare): string {
    if (!share?.shared_at) return '';
    const ms = Date.now() - share.shared_at.toDate().getTime();
    const totalMinutes = Math.floor(ms / 60_000);
    const hours = Math.floor(totalMinutes / 60);
    const days = Math.floor(hours / 24);
    if (days >= 2) return `${days} days ago`;
    if (days === 1) return 'yesterday';
    if (hours >= 1) return `${hours}h ago`;
    if (totalMinutes >= 1) return `${totalMinutes}m ago`;
    return 'just now';
  }

  formatShareExactTime(share: StudyShare): string {
    if (!share?.shared_at) return '';
    return share.shared_at.toDate().toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  }

  openShareHistoryModal(): void {
    const facilityNameMap = new Map<string, string>();
    (this.vm.facilities || []).forEach((f: Facility) => {
      if (f.id) facilityNameMap.set(f.id.toLowerCase(), f.name || f.id);
    });

    const rows: PatientShareHistoryModalData['facilityShares'] = [];
    this.patientSharesByFacility.forEach((share, fid) => {
      rows.push({ facilityId: fid, facilityName: facilityNameMap.get(fid) || fid, latestShare: share });
    });

    const data: PatientShareHistoryModalData = { facilityShares: rows };
    this.dialog.open(StudySharePatientHistoryModalComponent, {
      width: '700px',
      maxWidth: '95vw',
      maxHeight: '80vh',
      data,
    });
  }
}
